// Phase P-EXPAT-CITY-COMBOS (2026-06)
// City-specific overlay data for the /expat-relocation/$topic/$city combo pages.
// Each city ships ~1,200+ unique words across TH/EN so combo pages avoid duplicate
// content and rank for long-tail "<topic> in <city>" queries.

import type { ExpatCategory } from "./expat-relocation";

export interface CityCategorySnippet {
  /** 80-140 word city-specific intro for this category */
  introTh: string;
  introEn: string;
  /** 3-6 named venues/places in this city */
  placesTh: string[];
  placesEn: string[];
  /** Realistic local cost adjustment vs national average */
  costNoteTh: string;
  costNoteEn: string;
  /** 2-3 city-specific tips */
  tipsTh: string[];
  tipsEn: string[];
}

export interface ExpatCity {
  slug: string;
  nameTh: string;
  nameEn: string;
  region: string;
  lat: number;
  lng: number;
  /** 1-paragraph profile: who lives here, vibe, climate */
  profileTh: string;
  profileEn: string;
  /** Top expat communities in this city */
  expatCommunitiesTh: string;
  expatCommunitiesEn: string;
  /** Main districts/neighborhoods worth knowing */
  districtsTh: string[];
  districtsEn: string[];
  /** Transit / mobility summary */
  transitTh: string;
  transitEn: string;
  /** Climate / best season note */
  climateTh: string;
  climateEn: string;
  /** Internet & infrastructure */
  internetTh: string;
  internetEn: string;
  /** Cost-of-living index vs Bangkok (Bangkok = 100) */
  costIndex: number;
  /** Per-category overlay */
  categories: Record<ExpatCategory, CityCategorySnippet>;
}

export const EXPAT_CITIES: ExpatCity[] = [
  // ============= 1. BANGKOK =============
  {
    slug: "bangkok",
    nameTh: "กรุงเทพมหานคร",
    nameEn: "Bangkok",
    region: "กรุงเทพมหานคร",
    lat: 13.7563,
    lng: 100.5018,
    profileTh: "กรุงเทพฯ เป็นศูนย์กลางเอ็กซ์แพทใหญ่ที่สุดของไทย รองรับชาวต่างชาติกว่า 250,000 คน รวมนักธุรกิจ ครู Digital Nomad และครอบครัวที่ทำงานบริษัทข้ามชาติ พร้อม BTS/MRT 200+ สถานี และโรงพยาบาลระดับ JCI 15 แห่ง",
    profileEn: "Bangkok is Thailand's largest expat hub, home to 250,000+ foreigners including executives, teachers, digital nomads and corporate families, with 200+ BTS/MRT stations and 15 JCI-accredited hospitals.",
    expatCommunitiesTh: "ญี่ปุ่น (สุขุมวิท 33-49, ทองหล่อ-เอกมัย), อเมริกัน-ยุโรป (สาทร-สีลม-นิชดา), เกาหลี (พระราม 9, สุขุมวิท), อินเดีย (พหุรัด-สีลม), จีน (ห้วยขวาง-รัชดา)",
    expatCommunitiesEn: "Japanese (Sukhumvit 33-49, Thonglor-Ekkamai), American/European (Sathorn-Silom-Nichada), Korean (Rama 9, Sukhumvit), Indian (Phahurat-Silom), Chinese (Huai Khwang-Ratchada)",
    districtsTh: ["สุขุมวิท-อโศก", "สาทร-สีลม", "ทองหล่อ-เอกมัย", "อารีย์-พหลโยธิน", "พระราม 9-รัชดา", "นิชดาธานี-ปากเกร็ด"],
    districtsEn: ["Sukhumvit-Asoke", "Sathorn-Silom", "Thonglor-Ekkamai", "Ari-Phahonyothin", "Rama 9-Ratchada", "Nichada Thani-Pak Kret"],
    transitTh: "BTS Sukhumvit + Silom Lines, MRT Blue + Purple, ARL ไปสนามบินสุวรรณภูมิ, Grab/Bolt ทุกที่, รถยนต์ส่วนตัวต้องชินกับรถติด",
    transitEn: "BTS Sukhumvit + Silom Lines, MRT Blue + Purple, ARL to Suvarnabhumi, Grab/Bolt everywhere; private cars require tolerance for legendary traffic",
    climateTh: "ร้อนชื้นทั้งปี เฉลี่ย 28-35°C ฤดูฝน พ.ค.-ต.ค. ฤดูแล้ง พ.ย.-ก.พ.",
    climateEn: "Hot/humid year-round, 28-35°C avg. Rainy season May-Oct, cool/dry Nov-Feb",
    internetTh: "Fiber 1 Gbps (AIS/True/3BB) ทั่วเมือง 590-890 บาท/เดือน · 5G ทุก operator",
    internetEn: "1 Gbps fiber (AIS/True/3BB) THB 590-890/month citywide · 5G on all carriers",
    costIndex: 100,
    categories: {
      housing: {
        introTh: "ตลาดเช่าใหญ่ที่สุดในไทย — ตัวเลือกตั้งแต่ห้องสตูดิโอ 8,000 บาทย่านชานเมือง ถึงเพนต์เฮาส์ 200,000+ บาทใจกลางสุขุมวิท คอนโดใกล้ BTS/MRT คือ sweet spot ของเอ็กซ์แพทส่วนใหญ่ (1 ห้องนอน 18,000-35,000 บาท) ส่วนบ้านเดี่ยวมีในนิชดา (60,000-150,000 บาท) เหมาะครอบครัวที่มีลูกเรียน ISB",
        introEn: "Thailand's deepest rental market — studios from THB 8,000 in the suburbs to 200,000+ THB Sukhumvit penthouses. Condos along BTS/MRT are the expat sweet spot (1-bedroom THB 18,000-35,000); single houses in Nichada (THB 60,000-150,000) suit families with kids at ISB.",
        placesTh: ["The Lumpini Sukhumvit 24", "Park Origin Phromphong", "Noble Around Ari", "Whizdom Asoke-Sukhumvit", "Nichada Thani (เช่าบ้าน)", "Life Sukhumvit 62"],
        placesEn: ["The Lumpini Sukhumvit 24", "Park Origin Phromphong", "Noble Around Ari", "Whizdom Asoke-Sukhumvit", "Nichada Thani (houses)", "Life Sukhumvit 62"],
        costNoteTh: "แพงสุดในไทย — สุขุมวิท prime +30% เหนือเฉลี่ย เขตสีลม-สาทรเทียบเท่า",
        costNoteEn: "Most expensive in Thailand — prime Sukhumvit +30% vs average, Silom-Sathorn comparable",
        tipsTh: [
          "เลือกคอนโดในรัศมี 500 ม. จาก BTS/MRT ลดเวลาเดินทาง 40%",
          "ขอ TM30 จากเจ้าของก่อนย้ายเข้า — ใช้ขอ Certificate of Residence ต่อวีซ่า",
          "นายหน้าฟรีสำหรับผู้เช่า (เจ้าของจ่ายค่าคอม) — เช่น Live in Bangkok, Hipflat",
        ],
        tipsEn: [
          "Pick a condo within 500m of BTS/MRT to cut commute time 40%",
          "Get TM30 from the landlord on move-in — required for Certificate of Residence and visa extensions",
          "Tenant agents are free (the owner pays the commission) — try Live in Bangkok or Hipflat",
        ],
      },
      banking: {
        introTh: "กรุงเทพฯ เปิดบัญชีง่ายที่สุด — Bangkok Bank สาขาสีลม-อโศก-สุขุมวิทรับ tourist visa + work permit หรือ Long-Stay visa ส่วน Kasikorn K-Plus รองรับ English UI 100% และ SCB ทำงานร่วมกับ Wise/PayPal ดี รับเงินเดือนจากนายจ้างในไทยได้ทันที",
        introEn: "Bangkok is the easiest place to open an account — Bangkok Bank branches in Silom, Asoke, Sukhumvit accept tourist visa + work permit or long-stay visa. KBank K-Plus has full English UI and SCB pairs well with Wise/PayPal for receiving local payroll.",
        placesTh: ["Bangkok Bank Silom HQ", "KBank The Mall Bangkapi", "SCB Park Plaza", "Krungsri Ploenchit", "TTB Asoke", "UOB Sathorn"],
        placesEn: ["Bangkok Bank Silom HQ", "KBank The Mall Bangkapi", "SCB Park Plaza", "Krungsri Ploenchit", "TTB Asoke", "UOB Sathorn"],
        costNoteTh: "ค่าธรรมเนียมเดียวกันทั่วประเทศ — บัญชีออมทรัพย์ฟรี ATM ข้ามเขต 10 บาท/ครั้ง",
        costNoteEn: "Same fees nationwide — savings account is free, cross-region ATM THB 10/withdrawal",
        tipsTh: [
          "สาขาที่รับเอ็กซ์แพทดี: Bangkok Bank Q-House Lumpini, SCB เซ็นทรัลเอ็มบาสซี, KBank EmQuartier",
          "ใช้ Certificate of Residence (300 บาท ที่ สตม.) แทน Work Permit ได้ในหลายสาขา",
          "เปิดบัญชี USD/EUR ได้ที่ Bangkok Bank สาขาใหญ่ — โอนเข้าโดยไม่แลกเป็นบาทอัตโนมัติ",
        ],
        tipsEn: [
          "Expat-friendly branches: Bangkok Bank Q-House Lumpini, SCB Central Embassy, KBank EmQuartier",
          "A Certificate of Residence (THB 300 at Immigration) often substitutes for a work permit",
          "Open USD/EUR accounts at large Bangkok Bank branches — inbound wires aren't force-converted to THB",
        ],
      },
      schools: {
        introTh: "เลือกได้มากที่สุดในไทย — โรงเรียนนานาชาติ 80+ แห่ง ตั้งแต่ค่าเทอม 250,000 บาท (Wellington College Bangkok ไม่ใช่) ถึง 1,000,000+ บาท/ปี ISB, NIST, Bangkok Patana ครองอันดับ Top 3 ส่วน Shrewsbury, Brighton College, Harrow ตามมา หลักสูตร IB, British, American มีให้เลือก",
        introEn: "Widest choice in Thailand — 80+ international schools from THB 250,000 to 1,000,000+/year. ISB, NIST, Bangkok Patana lead the top tier; Shrewsbury, Brighton College, Harrow follow. IB, British and American curricula all available.",
        placesTh: ["International School Bangkok (ISB) - ปากเกร็ด", "NIST - สุขุมวิท 15", "Bangkok Patana - บางนา", "Shrewsbury Riverside", "Harrow Bangkok - ดอนเมือง", "St. Andrews Sukhumvit 107"],
        placesEn: ["International School Bangkok (ISB) - Pak Kret", "NIST - Sukhumvit 15", "Bangkok Patana - Bang Na", "Shrewsbury Riverside", "Harrow Bangkok - Don Mueang", "St. Andrews Sukhumvit 107"],
        costNoteTh: "Tier 1 (ISB/NIST/Patana): 800,000-1,100,000 · Tier 2 (Shrewsbury/Brighton): 600,000-900,000 · Tier 3 (Bromsgrove/Wellington): 400,000-650,000 บาท/ปี",
        costNoteEn: "Tier 1 (ISB/NIST/Patana): THB 800k-1.1M · Tier 2 (Shrewsbury/Brighton): THB 600-900k · Tier 3 (Bromsgrove/Wellington): THB 400-650k/year",
        tipsTh: [
          "สมัครล่วงหน้า 12-18 เดือนสำหรับ Tier 1 — มี waitlist ทุกระดับชั้น",
          "ขอ assessment test เด็กก่อนย้ายมา (online ได้) เพื่อรู้ว่าตรงระดับไหน",
          "Application fee 5,000-50,000 บาท ไม่คืนเงิน — ส่งสมัคร 2-3 ที่พร้อมกัน",
        ],
        tipsEn: [
          "Apply 12-18 months ahead for Tier 1 — waitlists exist at every grade",
          "Request a remote assessment before moving so you know the grade placement",
          "Application fees THB 5k-50k are non-refundable — apply to 2-3 schools in parallel",
        ],
      },
      healthcare: {
        introTh: "โรงพยาบาลระดับโลก 15 แห่ง — Bumrungrad, BNH, Samitivej, MedPark, Bangkok Hospital เป็น Top 5 พบ specialist วันเดียว ค่ารักษาถูกกว่า US/EU 50-70% แต่อย่ามองข้ามประกันสุขภาพ ค่ารักษามะเร็ง/หัวใจครั้งละ 500,000-3,000,000 บาท",
        introEn: "15 world-class hospitals — Bumrungrad, BNH, Samitivej, MedPark, Bangkok Hospital top the list. Same-day specialist visits cost 50-70% less than US/EU, but insurance is essential: cancer/cardiac care runs THB 500k-3M per episode.",
        placesTh: ["Bumrungrad International - สุขุมวิท 3", "BNH Hospital - สีลม", "Samitivej Sukhumvit - สุขุมวิท 49", "MedPark Hospital - คลองเตย", "Bangkok Hospital - เพชรบุรีตัดใหม่", "Mayo Clinic Care Network (RAM)"],
        placesEn: ["Bumrungrad International - Sukhumvit 3", "BNH Hospital - Silom", "Samitivej Sukhumvit - Sukhumvit 49", "MedPark Hospital - Khlong Toei", "Bangkok Hospital - New Phetchaburi", "Mayo Clinic Care Network (RAM)"],
        costNoteTh: "ตรวจสุขภาพ premium 8,000-25,000 · OPD specialist 1,500-3,500 · ห้องเดี่ยว 6,000-15,000/คืน",
        costNoteEn: "Premium check-up THB 8k-25k · OPD specialist THB 1,500-3,500 · private room THB 6,000-15,000/night",
        tipsTh: [
          "ประกันสุขภาพ international (Cigna/Allianz/AXA): 50,000-180,000 บาท/ปี ขึ้นกับอายุ",
          "Bumrungrad ทำ direct billing ได้กับประกัน 40+ บริษัท",
          "ใช้ MorDee/RaksaHealthcare consult online ค่าตรวจ 200-500 บาท — รับยาส่งบ้าน",
        ],
        tipsEn: [
          "International insurance (Cigna/Allianz/AXA): THB 50k-180k/year by age",
          "Bumrungrad direct-bills with 40+ insurers",
          "Use MorDee/Raksa Healthcare for THB 200-500 telehealth — prescriptions delivered",
        ],
      },
    },
  },

  // ============= 2. PHUKET =============
  {
    slug: "phuket",
    nameTh: "ภูเก็ต",
    nameEn: "Phuket",
    region: "ภาคใต้",
    lat: 7.8804,
    lng: 98.3923,
    profileTh: "เกาะภูเก็ตเป็นจุดหมายปลายทางอันดับ 1 สำหรับเอ็กซ์แพทเกษียณ นักลงทุน Elite Visa และ Digital Nomad — มีเอ็กซ์แพทกว่า 80,000 คน (รัสเซีย เยอรมัน อังกฤษ ออสซี่) สนามบินภูเก็ต (HKT) บินตรง 25 ประเทศ",
    profileEn: "Phuket is Thailand's #1 destination for retirees, Elite Visa investors and digital nomads — 80,000+ foreigners (Russian, German, British, Australian). Phuket Airport (HKT) connects 25 countries with direct flights.",
    expatCommunitiesTh: "รัสเซีย (ราไวย์-นาใน), อังกฤษ-ออสซี่ (เชิงทะเล-บางเทา), เยอรมัน (กะรน-กะตะ), อเมริกัน (ลากูน่า-เลย์), Digital Nomad (ราไวย์)",
    expatCommunitiesEn: "Russian (Rawai-Nai Harn), British/Australian (Cherng Talay-Bang Tao), German (Karon-Kata), American (Laguna-Layan), Digital Nomads (Rawai)",
    districtsTh: ["ป่าตอง (ท่องเที่ยว)", "เชิงทะเล-บางเทา (Laguna, ครอบครัว)", "ราไวย์-นาใน (Digital Nomad)", "กะรน-กะตะ (ครอบครัวสงบ)", "ภูเก็ตทาวน์ (เมืองเก่า)", "เกาะแก้ว (ใกล้สนามบิน)"],
    districtsEn: ["Patong (tourist)", "Cherng Talay-Bang Tao (Laguna, families)", "Rawai-Nai Harn (Digital Nomads)", "Karon-Kata (quiet families)", "Phuket Old Town", "Koh Kaew (near airport)"],
    transitTh: "รถยนต์ส่วนตัว/รถเช่าเป็นหลัก (ไม่มี BTS) — Grab/Bolt มีในตัวเมือง, สนามบิน-ป่าตอง 45 นาที, สนามบิน-ลากูน่า 20 นาที, มอเตอร์ไซค์เช่า 3,000 บาท/เดือน",
    transitEn: "Private/rental cars dominate (no BTS) — Grab/Bolt in town, airport-Patong 45 min, airport-Laguna 20 min; rental motorbikes THB 3,000/month",
    climateTh: "Tropical — มรสุม พ.ค.-ต.ค. (ฝนกระจาย ไม่ตลอดวัน) ฤดูท่องเที่ยวสูง พ.ย.-เม.ย. (อากาศแห้ง 26-32°C)",
    climateEn: "Tropical — monsoon May-Oct (scattered rain, not all-day), peak season Nov-Apr (dry, 26-32°C)",
    internetTh: "Fiber 500 Mbps-1 Gbps (AIS/True) 690-990 บาท ทั่วเกาะ · 5G ครอบคลุม 90%",
    internetEn: "500 Mbps-1 Gbps fiber (AIS/True) THB 690-990 islandwide · 5G covers 90%",
    costIndex: 90,
    categories: {
      housing: {
        introTh: "ภูเก็ตเป็นตลาดเช่ายาว vs สั้นชัดเจน — เช่ารายเดือน Long-stay (6-12 เดือน) ถูกกว่า Airbnb 50-70% วิลล่า private pool 3-bed 60,000-150,000 บาท/เดือนใน Laguna, คอนโด sea-view ราไวย์ 25,000-45,000 บาท ฤดูสูงห้องเต็มและขึ้น 2-3 เท่า — เซ็นสัญญายาวก่อน ต.ค.",
        introEn: "Phuket has a stark long-vs-short rental split — monthly long-stay (6-12 months) is 50-70% cheaper than Airbnb. A 3-bed private-pool villa runs THB 60-150k/month in Laguna; sea-view condos in Rawai THB 25-45k. High season fills up and rates 2-3x — sign before October.",
        placesTh: ["Laguna Park (วิลล่า)", "Allamanda Laguna", "The Title Rawai", "Zen Space Cherng Talay", "Royal Phuket Marina (มะลิ)", "Andamaya Surin Bay"],
        placesEn: ["Laguna Park (villas)", "Allamanda Laguna", "The Title Rawai", "Zen Space Cherng Talay", "Royal Phuket Marina", "Andamaya Surin Bay"],
        costNoteTh: "ราไวย์/ภูเก็ตทาวน์ -10% จากกรุงเทพ · Laguna/Surin +20% · Patong sea-view +40%",
        costNoteEn: "Rawai/Phuket Town -10% vs Bangkok · Laguna/Surin +20% · Patong sea-view +40%",
        tipsTh: [
          "เซ็นสัญญา 6 เดือน+ ก่อนต้นเดือนตุลาคมเพื่อล็อกราคา low season",
          "ค่าไฟใน Patong/ห้องนักท่องเที่ยว 7-8 บาท/หน่วย (สูงกว่า MEA)",
          "Foreign Quota คอนโด 49% เต็มเร็วในโครงการ Top — เช่ายาวก่อนซื้อ",
        ],
        tipsEn: [
          "Sign a 6+ month lease before early October to lock in low-season rates",
          "Patong/tourist-rate electricity is THB 7-8/unit (higher than MEA)",
          "49% foreign quotas fill fast in popular condos — rent before buying",
        ],
      },
      banking: {
        introTh: "ภูเก็ตเปิดบัญชียากกว่ากรุงเทพเล็กน้อย — ส่วนใหญ่ขอ Elite Visa, Retirement Visa, LTR หรือ Work Permit สาขาที่รับเอ็กซ์แพทดี: Bangkok Bank ภูเก็ตทาวน์, Kasikorn Central Phuket, SCB Boat Avenue บาง สาขาขอ Certificate of Residence เพิ่ม",
        introEn: "Phuket is slightly harder than Bangkok — most branches require Elite Visa, Retirement Visa, LTR or work permit. Expat-friendly: Bangkok Bank Phuket Town, KBank Central Phuket, SCB Boat Avenue. Some branches ask for a Certificate of Residence on top.",
        placesTh: ["Bangkok Bank Central Phuket", "Kasikorn Central Festival", "SCB Boat Avenue", "Krungsri Patong", "Krungthai Phuket Town", "TTB Jungceylon"],
        placesEn: ["Bangkok Bank Central Phuket", "KBank Central Festival", "SCB Boat Avenue", "Krungsri Patong", "Krungthai Phuket Town", "TTB Jungceylon"],
        costNoteTh: "เหมือนทั่วประเทศ — ATM นอกระบบ Phuket Town 20 บาท/ครั้ง",
        costNoteEn: "Same as nationwide — off-network ATM in Phuket Town THB 20/withdrawal",
        tipsTh: [
          "ใช้สำเนาสัญญาเช่ายาว 6 เดือน+ ทดแทน Work Permit ได้บางสาขา",
          "เปิดบัญชีดีที่สุดที่ Bangkok Bank Big C Phuket (มีพนักงาน English)",
          "Boat Avenue/Cherng Talay มีสาขาเอ็กซ์แพทเฉพาะ — นัดล่วงหน้า",
        ],
        tipsEn: [
          "A 6+ month lease can substitute for a work permit at some branches",
          "Best opening branch: Bangkok Bank Big C Phuket (English-speaking staff)",
          "Boat Avenue/Cherng Talay have dedicated expat desks — book ahead",
        ],
      },
      schools: {
        introTh: "ภูเก็ตมีโรงเรียนนานาชาติ 12+ แห่ง — Top 3: UWC Thailand (IB, 600,000 บาท), HeadStart (British, 500,000), BIS Phuket (British, 700,000) ส่วน Kajonkiet Hatyai, QSI, Berda เป็น mid-tier ทุกแห่งอยู่ในเชิงทะเล-บางเทา-เกาะแก้ว ใกล้ Laguna",
        introEn: "Phuket has 12+ international schools — Top 3: UWC Thailand (IB, THB 600k), HeadStart (British, 500k), BIS Phuket (British, 700k). Kajonkiet, QSI and Berda fill the mid-tier. Most cluster in Cherng Talay, Bang Tao and Koh Kaew near Laguna.",
        placesTh: ["UWC Thailand (เชิงทะเล)", "British International School Phuket (เกาะแก้ว)", "HeadStart International (เชิงทะเล)", "Berda Claude (กะทู้)", "Kajonkiet International (ป่าตอง)", "QSI (ภูเก็ตทาวน์)"],
        placesEn: ["UWC Thailand (Cherng Talay)", "British International School Phuket (Koh Kaew)", "HeadStart International (Cherng Talay)", "Berda Claude (Kathu)", "Kajonkiet International (Patong)", "QSI (Phuket Town)"],
        costNoteTh: "Top tier 500,000-800,000 · Mid 250,000-450,000 · ถูกกว่ากรุงเทพ 25-35%",
        costNoteEn: "Top tier THB 500-800k · Mid THB 250-450k · 25-35% cheaper than Bangkok",
        tipsTh: [
          "UWC Thailand รับ scholarship 30-100% สำหรับ DP — รีบสมัครก่อน 1 ปี",
          "BIS รับเด็กกลางเทอมได้ — ทำ assessment online ก่อน",
          "เลือกบ้านในรัศมี 15 นาทีจากโรงเรียน — รถติดเช้าเย็นไม่หนักเท่ากรุงเทพ",
        ],
        tipsEn: [
          "UWC Thailand offers 30-100% DP scholarships — apply 1 year ahead",
          "BIS admits mid-term — do a remote assessment first",
          "Pick housing within a 15-min drive — Phuket traffic is mild but tight at school runs",
        ],
      },
      healthcare: {
        introTh: "โรงพยาบาลเอกชน 5 แห่งหลัก: Bangkok Hospital Phuket, Bumrungrad Phuket (Phuket International), Siriroj, Mission, Dibuk — Bangkok Hospital Phuket เป็น Top tier มี ICU/cath lab ค่ารักษาถูกกว่ากรุงเทพ 15-20%",
        introEn: "5 main private hospitals: Bangkok Hospital Phuket, Bumrungrad Phuket (Phuket International), Siriroj, Mission, Dibuk. Bangkok Hospital Phuket leads with ICU/cath lab; care 15-20% cheaper than Bangkok.",
        placesTh: ["Bangkok Hospital Phuket (ภูเก็ตทาวน์)", "Phuket International Hospital", "Siriroj International (ภูเก็ตทาวน์)", "Mission Hospital Phuket (เชิงทะเล)", "Dibuk Hospital (ภูเก็ตทาวน์)", "Vachira Phuket (รัฐ)"],
        placesEn: ["Bangkok Hospital Phuket (Phuket Town)", "Phuket International Hospital", "Siriroj International (Phuket Town)", "Mission Hospital Phuket (Cherng Talay)", "Dibuk Hospital (Phuket Town)", "Vachira Phuket (public)"],
        costNoteTh: "OPD specialist 1,200-2,500 · ห้องเดี่ยว 5,000-10,000/คืน · ถูกกว่ากรุงเทพ 15%",
        costNoteEn: "OPD specialist THB 1,200-2,500 · private room THB 5,000-10,000/night · 15% cheaper than Bangkok",
        tipsTh: [
          "ฤดูสูง ห้องฉุกเฉินเต็มเร็ว — Bangkok Hospital Phuket รับ direct billing",
          "บัตรประกันโรคเขตร้อน (dengue, รถจักรยานยนต์อุบัติเหตุ) เพิ่ม rider",
          "Mission Hospital เชิงทะเล เหมาะครอบครัว Laguna — รับเด็ก",
        ],
        tipsEn: [
          "High season fills ERs fast — Bangkok Hospital Phuket direct-bills",
          "Add tropical-disease and motorbike-accident riders to your policy",
          "Mission Hospital in Cherng Talay is family-friendly for Laguna residents",
        ],
      },
    },
  },

  // ============= 3. CHIANG MAI =============
  {
    slug: "chiang-mai",
    nameTh: "เชียงใหม่",
    nameEn: "Chiang Mai",
    region: "ภาคเหนือ",
    lat: 18.7883,
    lng: 98.9853,
    profileTh: "เชียงใหม่เป็นเมืองหลวง Digital Nomad ของเอเชีย — ค่าครองชีพต่ำ 40% เทียบกรุงเทพ มีเอ็กซ์แพท 40,000+ คน (Digital Nomad อเมริกัน-ยุโรป, ครูภาษา, เกษียณญี่ปุ่น) co-working space 100+ แห่ง บินตรง 15 ประเทศ",
    profileEn: "Chiang Mai is Asia's digital-nomad capital — 40% cheaper than Bangkok with 40,000+ foreigners (American/European nomads, language teachers, Japanese retirees). 100+ co-working spaces and direct flights to 15 countries.",
    expatCommunitiesTh: "Digital Nomad (นิมมาน, สันติธรรม), อเมริกัน-ยุโรป (หางดง-แม่ริม), ญี่ปุ่น (สันทราย), จีน (สันกำแพง), เกษียณยุโรป (หางดง)",
    expatCommunitiesEn: "Digital nomads (Nimman, Santitham), American/European (Hang Dong-Mae Rim), Japanese (San Sai), Chinese (San Kamphaeng), European retirees (Hang Dong)",
    districtsTh: ["นิมมาน (Digital Nomad)", "ในเมือง (Old City)", "หางดง (ครอบครัว, สงบ)", "แม่ริม (วิลล่ารีโซวต์)", "สันทราย (มหาลัย)", "สันกำแพง (น้ำพุร้อน)"],
    districtsEn: ["Nimman (digital nomads)", "Old City", "Hang Dong (quiet families)", "Mae Rim (resorts/villas)", "San Sai (universities)", "San Kamphaeng (hot springs)"],
    transitTh: "รถยนต์/มอเตอร์ไซค์เป็นหลัก — Grab/Bolt มีในเมือง, มอเตอร์ไซค์เช่า 2,500-3,500 บาท/เดือน, ไม่มี BTS",
    transitEn: "Cars/motorbikes dominate — Grab/Bolt in town, rental motorbike THB 2,500-3,500/month, no BTS",
    climateTh: "เย็นกว่ากรุงเทพ — หนาว ธ.ค.-ก.พ. (15-25°C) ร้อน มี.ค.-พ.ค. (28-38°C) ฝน มิ.ย.-ต.ค. ระวัง PM2.5 เผาป่า ก.พ.-เม.ย.",
    climateEn: "Cooler than Bangkok — winter Dec-Feb (15-25°C), hot Mar-May (28-38°C), rain Jun-Oct. Beware PM2.5 burning season Feb-Apr",
    internetTh: "Fiber 1 Gbps (AIS/True/3BB) 590-790 บาท ทั่วเมือง · 5G ดี",
    internetEn: "1 Gbps fiber (AIS/True/3BB) THB 590-790 citywide · solid 5G",
    costIndex: 60,
    categories: {
      housing: {
        introTh: "ตลาดเช่าถูกที่สุดในไทย — สตูดิโอ 6,000-9,000 บาท, 1 ห้องนอน 10,000-18,000, วิลล่า 2 ห้องนอน 18,000-35,000 บาท นิมมาน + ในเมืองยอดนิยม Digital Nomad ส่วนหางดง-แม่ริมเหมาะครอบครัวที่อยู่บ้านเดี่ยว",
        introEn: "Cheapest rental market in Thailand — studios THB 6,000-9,000, 1-bed THB 10,000-18,000, 2-bed villas THB 18,000-35,000. Nimman and Old City suit nomads; Hang Dong-Mae Rim suit families wanting a standalone house.",
        placesTh: ["Stylish Condo Nimman", "The Astra Chang Klan", "Hillside Plaza", "Punna Residence", "Baan Wang Tan (วิลล่าหางดง)", "Esmeralda Arabian Ranches Mae Rim"],
        placesEn: ["Stylish Condo Nimman", "The Astra Chang Klan", "Hillside Plaza", "Punna Residence", "Baan Wang Tan (Hang Dong villas)", "Esmeralda Mae Rim"],
        costNoteTh: "ถูกกว่ากรุงเทพ 40-50% · นิมมาน premium +20% เหนือพื้นเมือง",
        costNoteEn: "40-50% cheaper than Bangkok · Nimman commands +20% vs the local baseline",
        tipsTh: [
          "เช่ายาว 6 เดือน+ ลดราคาเฉลี่ย 10-15%",
          "วิลล่าหางดง 3 ห้องนอน + สระว่ายน้ำ 25,000 บาท — เท่ากับสตูดิโอกรุงเทพ",
          "ระวังฝุ่นเดือน มี.ค. — เลือกห้องที่มีเครื่องฟอกอากาศ",
        ],
        tipsEn: [
          "6+ month leases trim 10-15% off the asking rent",
          "3-bed Hang Dong villas with a pool cost THB 25k — equal to a Bangkok studio",
          "Watch March smoke season — pick places with air purifiers",
        ],
      },
      banking: {
        introTh: "เปิดบัญชีง่ายกว่าภูเก็ต — Bangkok Bank สาขา Maya, Promenada, Central Festival และ Kasikorn Nimman เป็น expat-friendly รับ Tourist Visa + สัญญาเช่าหรือใบรับรองโรงเรียน (ครูภาษา) ก็พอ",
        introEn: "Easier than Phuket — Bangkok Bank branches at Maya, Promenada, Central Festival and KBank Nimman are expat-friendly, accepting a tourist visa + lease, or a school employment letter (for teachers).",
        placesTh: ["Bangkok Bank Maya Lifestyle", "Bangkok Bank Promenada", "Kasikorn Nimman", "SCB Central Festival", "Krungsri Airport Plaza", "Krungthai สี่แยกรินคำ"],
        placesEn: ["Bangkok Bank Maya Lifestyle", "Bangkok Bank Promenada", "KBank Nimman", "SCB Central Festival", "Krungsri Airport Plaza", "Krungthai Rin Kham"],
        costNoteTh: "ค่าธรรมเนียมเดียวกัน — Mae Hia/Hang Dong มี ATM น้อย เก็บค่าใช้บริการ 20 บาท",
        costNoteEn: "Same fees — Mae Hia/Hang Dong have fewer ATMs, off-network THB 20",
        tipsTh: [
          "ครูภาษา TEFL ใช้ใบรับรองโรงเรียนเปิดบัญชีได้",
          "Bangkok Bank Maya มี English-speaking expat desk ทุกวัน",
          "เปิดบัญชี Wise + KBank ดีสำหรับโอนเงินจากต่างประเทศ",
        ],
        tipsEn: [
          "TEFL teachers can open accounts with a school employment letter",
          "Bangkok Bank Maya has a daily English-speaking expat desk",
          "Wise + KBank pairing works well for inbound transfers",
        ],
      },
      schools: {
        introTh: "เชียงใหม่มีโรงเรียนนานาชาติ 8 แห่ง — Top 3: Prem Tinsulanonda (IB, แม่ริม, 600,000), Chiang Mai International School (American, 350,000), Lanna International (British, 280,000) ค่าเทอมถูกกว่ากรุงเทพ 40-50% — เหมาะครอบครัวที่ทำงาน remote",
        introEn: "8 international schools — Top 3: Prem Tinsulanonda (IB, Mae Rim, THB 600k), Chiang Mai International School (American, 350k), Lanna International (British, 280k). Tuition is 40-50% cheaper than Bangkok — ideal for remote-working families.",
        placesTh: ["Prem Tinsulanonda International - แม่ริม", "Chiang Mai International School (CMIS) - ในเมือง", "Lanna International School (LIST)", "Nakornpayap International (NIS)", "American Pacific International School (APIS)", "Grace International - หางดง"],
        placesEn: ["Prem Tinsulanonda International - Mae Rim", "Chiang Mai International School (CMIS)", "Lanna International School (LIST)", "Nakornpayap International (NIS)", "American Pacific International School (APIS)", "Grace International - Hang Dong"],
        costNoteTh: "Top tier 450,000-650,000 · Mid 220,000-380,000 · ถูกที่สุดในเครือ Top expat",
        costNoteEn: "Top tier THB 450-650k · Mid THB 220-380k · cheapest among major expat hubs",
        tipsTh: [
          "Prem รับ boarder ตั้งแต่ Year 7 — ครอบครัวอยู่กรุงเทพยังส่งได้",
          "Lanna British curriculum — เด็กกลับ UK แล้วต่อ A-Level ราบรื่น",
          "หางดง 20 นาทีจาก Prem — เลือกบ้านเช่าใกล้โรงเรียน",
        ],
        tipsEn: [
          "Prem boards from Year 7 — Bangkok families can still enroll",
          "Lanna's British track transitions cleanly to UK A-Levels",
          "Hang Dong is 20 min from Prem — pick rentals near the school",
        ],
      },
      healthcare: {
        introTh: "Chiang Mai Ram, Bangkok Hospital Chiang Mai, Lanna Hospital เป็น Top 3 เอกชน — มาตรฐาน international, ราคาถูกกว่ากรุงเทพ 30-40% Chiang Mai Ram เก่งสุดด้านโรคทั่วไป Bangkok Hospital Chiang Mai เก่งเฉพาะทาง",
        introEn: "Chiang Mai Ram, Bangkok Hospital Chiang Mai and Lanna Hospital lead — international standard, 30-40% cheaper than Bangkok. Chiang Mai Ram is best for general care; Bangkok Hospital CM for specialists.",
        placesTh: ["Chiang Mai Ram Hospital - ในเมือง", "Bangkok Hospital Chiang Mai - ซุปเปอร์ไฮเวย์", "Lanna Hospital - ซุปเปอร์ไฮเวย์", "McCormick (มหิดล)", "Suandok (รัฐ, มช.)", "Rajavej Hospital"],
        placesEn: ["Chiang Mai Ram Hospital - Old City", "Bangkok Hospital Chiang Mai - Superhighway", "Lanna Hospital - Superhighway", "McCormick (Mahidol)", "Suandok (public, CMU)", "Rajavej Hospital"],
        costNoteTh: "OPD 800-1,800 · ห้องเดี่ยว 3,500-7,000/คืน · ถูกกว่ากรุงเทพ 35%",
        costNoteEn: "OPD THB 800-1,800 · private room THB 3,500-7,000/night · 35% cheaper than Bangkok",
        tipsTh: [
          "Suandok (มช.) คุณภาพรัฐดีที่สุดในภาคเหนือ — ค่าตรวจ 300-800",
          "ระวัง dengue ตลอดปี — ฉีดวัคซีน Qdenga ที่ Bangkok Hospital",
          "ฤดูฝุ่น มี.ค.-เม.ย. จองเช็คอัพ lung function ล่วงหน้า",
        ],
        tipsEn: [
          "Suandok (CMU) is the North's best public hospital — visits THB 300-800",
          "Dengue is year-round — get Qdenga at Bangkok Hospital",
          "Book lung-function check-ups before March-April smoke season",
        ],
      },
    },
  },

  // ============= 4. PATTAYA =============
  {
    slug: "pattaya",
    nameTh: "พัทยา",
    nameEn: "Pattaya",
    region: "ภาคตะวันออก",
    lat: 12.9236,
    lng: 100.8825,
    profileTh: "พัทยา-จอมเทียนเป็นเมืองชายฝั่งที่มีเอ็กซ์แพท 60,000+ คน หลักคือเกษียณรัสเซีย ยุโรป อังกฤษ และนักลงทุน EEC ห่างกรุงเทพ 1.5 ชั่วโมง สนามบินอู่ตะเภาบินตรง 6 ประเทศ",
    profileEn: "Pattaya-Jomtien is a coastal city with 60,000+ foreigners — mostly Russian, European and British retirees plus EEC investors. 1.5 hours from Bangkok; U-Tapao airport offers direct flights to 6 countries.",
    expatCommunitiesTh: "รัสเซีย (พัทยากลาง), อังกฤษ-อเมริกัน (จอมเทียน), เยอรมัน-สวิส (พัทยาเหนือ), อินเดีย (พัทยากลาง), จีน (เกาะล้าน)",
    expatCommunitiesEn: "Russian (Central Pattaya), British/American (Jomtien), German/Swiss (North Pattaya), Indian (Central), Chinese (Koh Larn)",
    districtsTh: ["พัทยาเหนือ (สงบ)", "พัทยากลาง (Walking Street)", "พัทยาใต้-จอมเทียน (เกษียณ)", "ห้วยใหญ่ (วิลล่า)", "นาจอมเทียน (sea-view)", "บางเสร่"],
    districtsEn: ["North Pattaya (quiet)", "Central Pattaya (Walking Street)", "South Pattaya-Jomtien (retirees)", "Huai Yai (villas)", "Na Jomtien (sea-view)", "Bang Saray"],
    transitTh: "Baht bus 10-30 บาทเส้นหลัก, Grab/Bolt มี, มอเตอร์ไซค์เช่า 2,500/เดือน, สนามบินสุวรรณภูมิ 1.5 ชม. ผ่านมอเตอร์เวย์",
    transitEn: "Baht buses THB 10-30 on main routes, Grab/Bolt available, motorbike rentals THB 2,500/month, Suvarnabhumi 1.5h via motorway",
    climateTh: "ร้อนชื้น 27-34°C ทั้งปี ฝน พ.ค.-ต.ค. (ฝนแรงไม่นาน) ฤดูสูง พ.ย.-มี.ค.",
    climateEn: "Hot/humid 27-34°C year-round, rain May-Oct (intense short bursts), high season Nov-Mar",
    internetTh: "Fiber 1 Gbps 690-890 บาท · 5G ครอบคลุม Beach Road, Jomtien",
    internetEn: "1 Gbps fiber THB 690-890 · 5G covers Beach Road, Jomtien",
    costIndex: 70,
    categories: {
      housing: {
        introTh: "พัทยาตลาดเช่าหลากหลาย — สตูดิโอ 7,000-12,000, 1 ห้องนอน sea-view 15,000-25,000, วิลล่าห้วยใหญ่ 3 ห้องนอน + สระ 35,000-65,000 บาท/เดือน เซ็นยาวลด 15-20% ระวัง Foreign Quota 49% เต็มเร็วในคอนโดน่าซื้อ",
        introEn: "Diverse rental market — studios THB 7-12k, 1-bed sea-view THB 15-25k, 3-bed Huai Yai villas with pool THB 35-65k/month. Long leases save 15-20%. The 49% foreign quota fills fast in popular condos.",
        placesTh: ["The Riviera Wongamat", "Centric Sea Pattaya", "Lumpini Jomtien", "Park Royal 2", "Mountain View Huai Yai (วิลล่า)", "Baan Sansaran"],
        placesEn: ["The Riviera Wongamat", "Centric Sea Pattaya", "Lumpini Jomtien", "Park Royal 2", "Mountain View Huai Yai (villas)", "Baan Sansaran"],
        costNoteTh: "ถูกกว่ากรุงเทพ 30% · sea-view Wongamat premium · จอมเทียน mid-range",
        costNoteEn: "30% cheaper than Bangkok · Wongamat sea-view at premium · Jomtien mid-range",
        tipsTh: [
          "เลือกคอนโดที่อนุญาตเช่ายาว (บางโครงการบังคับ short-stay เท่านั้น)",
          "วิลล่าห้วยใหญ่/นาจอมเทียน 30-50% ถูกกว่าวิลล่าภูเก็ตขนาดเท่ากัน",
          "เซ็นต์สัญญาก่อน ต.ค. — ฤดูสูง พ.ย.-มี.ค. ราคาขึ้น 30-50%",
        ],
        tipsEn: [
          "Pick condos that allow long leases (some buildings are short-stay only)",
          "Huai Yai/Na Jomtien villas are 30-50% cheaper than equivalent Phuket villas",
          "Sign before October — high season Nov-Mar bumps rates 30-50%",
        ],
      },
      banking: {
        introTh: "Bangkok Bank พัทยากลาง + Central Festival, KBank Royal Garden, SCB Avenue เปิดง่ายสำหรับเกษียณ — ต้องการสัญญาเช่ายาว 6 เดือน + Retirement Visa หรือ Elite Visa บางสาขาขอ Certificate of Residence จาก สตม. พัทยา",
        introEn: "Bangkok Bank Central Pattaya + Central Festival, KBank Royal Garden, SCB Avenue are retiree-friendly — require a 6+ month lease + retirement or Elite Visa. Some branches ask for a Certificate of Residence from Pattaya Immigration.",
        placesTh: ["Bangkok Bank Central Festival", "Bangkok Bank Big C Pattaya", "Kasikorn Royal Garden Plaza", "SCB Avenue Pattaya", "Krungsri Pattaya Beach", "Krungthai Sukhumvit Pattaya"],
        placesEn: ["Bangkok Bank Central Festival", "Bangkok Bank Big C Pattaya", "KBank Royal Garden Plaza", "SCB Avenue Pattaya", "Krungsri Pattaya Beach", "Krungthai Sukhumvit Pattaya"],
        costNoteTh: "เหมือนกรุงเทพ — บางสาขามีค่าตรวจสอบเอกสาร 500 บาทครั้งแรก",
        costNoteEn: "Same as Bangkok — some branches charge a THB 500 first-time document fee",
        tipsTh: [
          "Pattaya Immigration ออก Certificate of Residence ใน 1 วัน ค่า 300 บาท",
          "Bangkok Bank Royal Garden มี Russian-speaking staff",
          "Elite Visa เปิดบัญชีง่ายสุด — ใช้ใบ Elite + passport",
        ],
        tipsEn: [
          "Pattaya Immigration issues Certificate of Residence same-day for THB 300",
          "Bangkok Bank Royal Garden has Russian-speaking staff",
          "Elite Visa is easiest — Elite letter + passport opens an account",
        ],
      },
      schools: {
        introTh: "พัทยามีโรงเรียนนานาชาติ 7 แห่ง — Top: Regents International School Pattaya (British, 480,000), Mooltripakdee International (American, 320,000), GIS Pattaya (350,000) เหมาะครอบครัว EEC ทำงานนิคมระยอง-ชลบุรี",
        introEn: "7 international schools — Top: Regents International School Pattaya (British, THB 480k), Mooltripakdee International (American, 320k), GIS Pattaya (350k). Ideal for EEC families working in Rayong/Chonburi estates.",
        placesTh: ["Regents International School Pattaya - ห้วยใหญ่", "Mooltripakdee International (MIS) - พัทยากลาง", "GIS Pattaya - พัทยาเหนือ", "ISP International School", "Tara Pattana International", "Garden International"],
        placesEn: ["Regents International School Pattaya - Huai Yai", "Mooltripakdee International (MIS) - Central Pattaya", "GIS Pattaya - North Pattaya", "ISP International School", "Tara Pattana International", "Garden International"],
        costNoteTh: "Top 380,000-550,000 · Mid 220,000-350,000 · ถูกกว่ากรุงเทพ 35%",
        costNoteEn: "Top THB 380-550k · Mid THB 220-350k · 35% cheaper than Bangkok",
        tipsTh: [
          "Regents มี boarding (Year 7+) สำหรับครอบครัวอยู่ EEC factory",
          "MIS รับเด็กกลางเทอม assessment ที่โรงเรียนใน 1 วัน",
          "เลือกบ้านในห้วยใหญ่-นาจอมเทียน 10 นาทีจาก Regents",
        ],
        tipsEn: [
          "Regents boards from Year 7 — handy for EEC factory families",
          "MIS admits mid-term with same-day onsite assessment",
          "Pick Huai Yai/Na Jomtien housing within a 10-min drive of Regents",
        ],
      },
      healthcare: {
        introTh: "Bangkok Hospital Pattaya เป็น Top tier — ICU, cath lab, dialysis ครบ; Pattaya Memorial Hospital เก่าแก่ราคาประหยัด; Bangkok Hospital Jomtien สำหรับ South Pattaya ค่ารักษาถูกกว่ากรุงเทพ 20%",
        introEn: "Bangkok Hospital Pattaya is top tier — full ICU, cath lab, dialysis. Pattaya Memorial Hospital is older but cheaper; Bangkok Hospital Jomtien serves South Pattaya. Care 20% cheaper than Bangkok.",
        placesTh: ["Bangkok Hospital Pattaya - พัทยากลาง", "Bangkok Hospital Jomtien", "Pattaya Memorial Hospital", "Pattaya International Hospital", "Banglamung Hospital (รัฐ)", "Sirikit Naval (รัฐ)"],
        placesEn: ["Bangkok Hospital Pattaya - Central", "Bangkok Hospital Jomtien", "Pattaya Memorial Hospital", "Pattaya International Hospital", "Banglamung Hospital (public)", "Sirikit Naval (public)"],
        costNoteTh: "OPD 1,000-2,200 · ห้องเดี่ยว 4,000-9,000/คืน · ถูกกว่ากรุงเทพ 20%",
        costNoteEn: "OPD THB 1,000-2,200 · private room THB 4,000-9,000/night · 20% cheaper than Bangkok",
        tipsTh: [
          "Bangkok Hospital Pattaya รับ direct billing 30+ ประกัน",
          "Marine Casualty หลังดำน้ำ-สกูตเตอร์ — ห้องฉุกเฉินเปิด 24/7",
          "Banglamung รัฐคุณภาพดี ใช้ประกัน Social Security ได้",
        ],
        tipsEn: [
          "Bangkok Hospital Pattaya direct-bills 30+ insurers",
          "Diving/scooter injuries — 24/7 ER on Sukhumvit",
          "Banglamung public hospital accepts Social Security insurance",
        ],
      },
    },
  },

  // ============= 5. HUA HIN =============
  {
    slug: "hua-hin",
    nameTh: "หัวหิน",
    nameEn: "Hua Hin",
    region: "ภาคตะวันตก",
    lat: 12.5684,
    lng: 99.9577,
    profileTh: "หัวหินเป็นเมืองเกษียณยอดนิยมอันดับ 2 รองจากพัทยา — สงบ ปลอดภัย ใกล้กรุงเทพ 2.5 ชม. มีเอ็กซ์แพทเกษียณ 25,000 คน (สวีเดน นอร์เวย์ เดนมาร์ก เยอรมัน อังกฤษ) สนามบินหัวหินบินตรงไม่กี่เมือง",
    profileEn: "Hua Hin is the #2 retirement city after Pattaya — calm, safe, 2.5h from Bangkok. ~25,000 expat retirees (Swedish, Norwegian, Danish, German, British). Hua Hin Airport offers limited direct flights.",
    expatCommunitiesTh: "สแกนดิเนเวีย (กลางเมือง-เขาตะเกียบ), อังกฤษ-เยอรมัน (เขาตะเกียบ-หาดทรายน้อย), สวิส-ฝรั่งเศส (ปราณบุรี)",
    expatCommunitiesEn: "Scandinavian (downtown-Khao Takiab), British/German (Khao Takiab-Sai Noi Beach), Swiss/French (Pran Buri)",
    districtsTh: ["กลางเมืองหัวหิน", "เขาตะเกียบ (sea-view)", "หาดทรายน้อย-หาดน้อย", "ปราณบุรี (สงบ)", "ชะอำ", "สวนสนประดิพัทธ์"],
    districtsEn: ["Hua Hin Downtown", "Khao Takiab (sea-view)", "Sai Noi-Hat Noi", "Pran Buri (quiet)", "Cha-am", "Suan Son Pradipat"],
    transitTh: "รถยนต์ส่วนตัวเป็นหลัก — Grab มีน้อย, มอเตอร์ไซค์เช่า 2,000-3,000 บาท/เดือน, รถตู้ไปกรุงเทพทุก 30 นาที 200 บาท",
    transitEn: "Cars dominate — limited Grab, motorbike rentals THB 2,000-3,000/month, vans to Bangkok every 30 min for THB 200",
    climateTh: "อากาศดีที่สุดในไทย — แห้งกว่ากรุงเทพ 25-32°C ฤดูฝนสั้น (ก.ย.-ต.ค.) ฤดูสูง พ.ย.-เม.ย.",
    climateEn: "Best climate in Thailand — drier than Bangkok, 25-32°C, short rainy season (Sep-Oct), high season Nov-Apr",
    internetTh: "Fiber 1 Gbps 690-790 บาท · 5G ครอบคลุมเขาตะเกียบ-กลางเมือง",
    internetEn: "1 Gbps fiber THB 690-790 · 5G covers Khao Takiab-downtown",
    costIndex: 75,
    categories: {
      housing: {
        introTh: "ตลาดเช่าเน้นเกษียณ — สตูดิโอ 7,000-12,000, คอนโด sea-view 1 ห้องนอน 15,000-28,000, วิลล่า private pool 3 ห้องนอน 30,000-60,000 บาท เซ็นยาว 12 เดือนลด 10-15% หา villa rental Hua Hin Property Group หรือ Hua Hin Realty",
        introEn: "Retiree-skewed market — studios THB 7-12k, 1-bed sea-view THB 15-28k, 3-bed private-pool villas THB 30-60k. 12-month leases save 10-15%. Try Hua Hin Property Group or Hua Hin Realty for villas.",
        placesTh: ["Baan San Ploen", "My Resort Hua Hin", "Marrakesh Residence", "Black Mountain Golf Resort (วิลล่า)", "The Energy Hua Hin", "Banyan Resort Hua Hin"],
        placesEn: ["Baan San Ploen", "My Resort Hua Hin", "Marrakesh Residence", "Black Mountain Golf Resort (villas)", "The Energy Hua Hin", "Banyan Resort Hua Hin"],
        costNoteTh: "ถูกกว่ากรุงเทพ 25% · sea-view เขาตะเกียบ premium · ปราณบุรี mid",
        costNoteEn: "25% cheaper than Bangkok · Khao Takiab sea-view at premium · Pran Buri mid-range",
        tipsTh: [
          "วิลล่า Black Mountain มี golf membership — เหมาะนักเล่นกอล์ฟ",
          "เซ็นต์ก่อน ก.ย. — ฤดูสูงรัสเซีย/สแกนดิ ราคาขึ้น 40%",
          "ค่าน้ำค่าไฟวิลล่าใหญ่กว่าคอนโด — เตรียม 5,000-8,000/เดือน",
        ],
        tipsEn: [
          "Black Mountain villas come with golf membership — perfect for golfers",
          "Sign before September — Russian/Scandi season pushes rates +40%",
          "Villa utilities cost more than condos — budget THB 5,000-8,000/month",
        ],
      },
      banking: {
        introTh: "Bangkok Bank Hua Hin Market Village, KBank BluPort, SCB Tesco Lotus เปิดบัญชีง่ายสุด — รับ Retirement Visa + lease 6 เดือน บางสาขามีพนักงานพูดเยอรมัน/สวีเดน",
        introEn: "Bangkok Bank Hua Hin Market Village, KBank BluPort, SCB Tesco Lotus are the easiest — they accept retirement visa + 6-month lease. Some branches have German/Swedish-speaking staff.",
        placesTh: ["Bangkok Bank Market Village", "Bangkok Bank BluPort", "Kasikorn BluPort", "SCB Tesco Lotus Hua Hin", "Krungsri Hua Hin", "Krungthai Hua Hin Plaza"],
        placesEn: ["Bangkok Bank Market Village", "Bangkok Bank BluPort", "KBank BluPort", "SCB Tesco Lotus Hua Hin", "Krungsri Hua Hin", "Krungthai Hua Hin Plaza"],
        costNoteTh: "เหมือนกรุงเทพ — ATM ในชะอำ-ปราณบุรีน้อย",
        costNoteEn: "Same as Bangkok — fewer ATMs in Cha-am/Pran Buri",
        tipsTh: [
          "Bangkok Bank Market Village มีพนักงานพูดอังกฤษ-เยอรมันประจำ",
          "Retirement Visa + 800,000 บาทในบัญชีไทย = เปิด KBank ได้ใน 1 วัน",
          "เปิดบัญชี Fixed Deposit 12 เดือน — ใช้ต่อ Retirement Visa",
        ],
        tipsEn: [
          "Bangkok Bank Market Village has dedicated English-/German-speaking staff",
          "Retirement visa + THB 800k in a Thai account opens KBank same-day",
          "Open a 12-month fixed deposit — it doubles as retirement-visa proof",
        ],
      },
      schools: {
        introTh: "หัวหินมีโรงเรียนนานาชาติ 3 แห่ง — International School Eastern Seaboard ISE (British, 380,000), Hua Hin International School (American, 280,000), Brighton College Pattaya/Hua Hin satellite ส่วนใหญ่เป็นครอบครัวคนรุ่นใหม่ที่ทำงาน remote",
        introEn: "3 international schools — International School Eastern Seaboard (ISE, British, THB 380k), Hua Hin International School (American, 280k), Brighton College Pattaya/Hua Hin satellite. Mostly young remote-working families.",
        placesTh: ["International School Hua Hin (ISH) - หัวหิน", "Hua Hin Vitthaya International", "Mountain Creek School", "Brighton College (สาขา ใกล้)", "Wells International (สาขา ชะอำ)", "American Pacific (สาขา)"],
        placesEn: ["International School Hua Hin (ISH)", "Hua Hin Vitthaya International", "Mountain Creek School", "Brighton College (nearby)", "Wells International (Cha-am branch)", "American Pacific (branch)"],
        costNoteTh: "Top 350,000-450,000 · Mid 200,000-300,000 · ถูกกว่ากรุงเทพ 45%",
        costNoteEn: "Top THB 350-450k · Mid THB 200-300k · 45% cheaper than Bangkok",
        tipsTh: [
          "Hua Hin International School รับ TEFL teacher จากต่างประเทศ — ลูกครู discount 30%",
          "Mountain Creek เปิดใหม่ทุนต่างประเทศ — IB diploma ปี 2027",
          "เลือกบ้านในรัศมี 10 นาที — ไม่มีรถโรงเรียน",
        ],
        tipsEn: [
          "Hua Hin International School recruits foreign TEFL teachers — staff children get 30% off",
          "Mountain Creek is new with foreign capital — IB diploma launching 2027",
          "Pick housing within a 10-min drive — no school buses",
        ],
      },
      healthcare: {
        introTh: "Bangkok Hospital Hua Hin เป็น Top tier — เก่งโรคหัวใจ/ออร์โธปิดิกส์ (golf injury) San Paulo Hospital เน้นเกษียณ Hua Hin Hospital รัฐคุณภาพดีสำหรับ emergency",
        introEn: "Bangkok Hospital Hua Hin is top tier — strong cardiology/orthopedics (golf injuries). San Paulo Hospital caters to retirees; Hua Hin Hospital (public) is reliable for emergencies.",
        placesTh: ["Bangkok Hospital Hua Hin", "San Paulo Hospital Hua Hin", "Hua Hin Hospital (รัฐ)", "Cha-am Hospital", "Pran Buri Hospital", "Vibhavadi Hua Hin"],
        placesEn: ["Bangkok Hospital Hua Hin", "San Paulo Hospital Hua Hin", "Hua Hin Hospital (public)", "Cha-am Hospital", "Pran Buri Hospital", "Vibhavadi Hua Hin"],
        costNoteTh: "OPD 900-1,800 · ห้องเดี่ยว 3,500-7,500/คืน · ถูกกว่ากรุงเทพ 25%",
        costNoteEn: "OPD THB 900-1,800 · private room THB 3,500-7,500/night · 25% cheaper than Bangkok",
        tipsTh: [
          "Bangkok Hospital Hua Hin รับ direct billing 25+ ประกัน + ภาษา ดัตช์/เยอรมัน",
          "San Paulo มีคลินิกเกษียณเฉพาะ — annual check-up package 8,500 บาท",
          "ปราณบุรีมีคลินิกตา-ฟัน — ราคาประหยัด ครึ่งหนึ่งของ Bangkok Hospital",
        ],
        tipsEn: [
          "Bangkok Hospital Hua Hin direct-bills 25+ insurers and offers Dutch/German service",
          "San Paulo has a retiree clinic — annual check-up package THB 8,500",
          "Pran Buri has eye/dental clinics — half the price of Bangkok Hospital",
        ],
      },
    },
  },

  // ============= 6. KOH SAMUI =============
  {
    slug: "koh-samui",
    nameTh: "เกาะสมุย",
    nameEn: "Koh Samui",
    region: "ภาคใต้",
    lat: 9.5018,
    lng: 99.9648,
    profileTh: "เกาะสมุยเป็นเกาะอันดับ 2 รองจากภูเก็ตในด้านเอ็กซ์แพท — มี ~25,000 คน (อังกฤษ ฝรั่งเศส เยอรมัน ออสซี่) สนามบินสมุย (USM) บินตรง 5 ประเทศ ค่าครองชีพต่ำกว่าภูเก็ต 10%",
    profileEn: "Koh Samui is Thailand's #2 expat island after Phuket — ~25,000 foreigners (British, French, German, Australian). Samui Airport (USM) has direct flights to 5 countries. 10% cheaper than Phuket.",
    expatCommunitiesTh: "อังกฤษ-ออสซี่ (เฉวง-บ่อผุด), ฝรั่งเศส (แม่น้ำ-ลิปะน้อย), เยอรมัน (ละไม), Digital Nomad (บ่อผุด)",
    expatCommunitiesEn: "British/Australian (Chaweng-Bophut), French (Maenam-Lipa Noi), German (Lamai), Digital Nomads (Bophut)",
    districtsTh: ["เฉวง (ท่องเที่ยว, sea-view)", "บ่อผุด-Fisherman's Village (Digital Nomad)", "ละไม (ครอบครัว)", "แม่น้ำ (สงบ)", "ลิปะน้อย (เกษียณ)", "ตลิ่งงาม (จุดชมพระอาทิตย์ตก)"],
    districtsEn: ["Chaweng (tourist, sea-view)", "Bophut-Fisherman's Village (digital nomads)", "Lamai (families)", "Maenam (quiet)", "Lipa Noi (retirees)", "Taling Ngam (sunset)"],
    transitTh: "รถยนต์/มอเตอร์ไซค์ — ไม่มี Grab/Bolt บนเกาะ (ใช้แท็กซี่ท้องถิ่นแพง) มอเตอร์ไซค์เช่า 2,500-3,500 บาท/เดือน",
    transitEn: "Cars/motorbikes — no Grab/Bolt on the island (local taxis are expensive), motorbike rentals THB 2,500-3,500/month",
    climateTh: "Tropical — ฝน ต.ค.-ธ.ค. (มรสุมตะวันออกเฉียงเหนือ) ฤดูสูง ม.ค.-ก.ย.",
    climateEn: "Tropical — rain Oct-Dec (NE monsoon), high season Jan-Sep",
    internetTh: "Fiber 500 Mbps-1 Gbps 790-1,290 บาท · 5G เฉวง-บ่อผุด",
    internetEn: "500 Mbps-1 Gbps fiber THB 790-1,290 · 5G in Chaweng-Bophut",
    costIndex: 85,
    categories: {
      housing: {
        introTh: "ตลาดเช่าเล็ก แต่ตัวเลือกหลากหลาย — สตูดิโอ 9,000-15,000, วิลล่า private pool 3 ห้องนอน 40,000-90,000 บาท/เดือน ราคาเดียวกับภูเก็ตแต่ตัวเลือกน้อยกว่า มีบริการ long-stay villa เช่น Conrad Residence, Six Senses",
        introEn: "Small but diverse rental market — studios THB 9-15k, 3-bed private-pool villas THB 40-90k/month. Prices comparable to Phuket but fewer choices. Long-stay villas at Conrad Residence, Six Senses, etc.",
        placesTh: ["Bandara Villas (บ่อผุด)", "Replay Residence (บ่อผุด)", "Beach Republic", "Conrad Residence Samui", "Six Senses Residence", "Botanica Luxury Villas"],
        placesEn: ["Bandara Villas (Bophut)", "Replay Residence (Bophut)", "Beach Republic", "Conrad Residence Samui", "Six Senses Residence", "Botanica Luxury Villas"],
        costNoteTh: "เทียบเท่าภูเก็ต — ค่าไฟเรท foreigner 7-9 บาท/หน่วย",
        costNoteEn: "Comparable to Phuket — foreigner-rate electricity THB 7-9/unit",
        tipsTh: [
          "วิลล่าใน Plai Laem-บ่อผุด ใกล้สนามบิน + Fisherman's Village",
          "เซ็นต์ก่อน ก.ย. ราคาฤดูแล้ง ม.ค.-มี.ค. ขึ้น 50-80%",
          "ขนของเข้าเกาะ shipping 5,000-15,000 บาท — ใช้บริการ Anywhere Movers",
        ],
        tipsEn: [
          "Plai Laem-Bophut villas are close to the airport + Fisherman's Village",
          "Sign before September — dry-season rates (Jan-Mar) jump 50-80%",
          "Shipping in furniture costs THB 5,000-15,000 — use Anywhere Movers",
        ],
      },
      banking: {
        introTh: "เกาะสมุยมี Bangkok Bank, KBank, SCB สาขาในเฉวง, ละไม, นาทอน เปิดบัญชียากกว่าภูเก็ต — ขอ Long-stay visa + lease 6 เดือน + Certificate of Residence",
        introEn: "Bangkok Bank, KBank, SCB have branches in Chaweng, Lamai, Nathon. Opening is harder than Phuket — requires long-stay visa + 6-month lease + Certificate of Residence.",
        placesTh: ["Bangkok Bank Tesco Lotus Chaweng", "Bangkok Bank Central Festival Samui", "KBank Central Festival", "SCB Chaweng", "Krungsri Lamai", "Krungthai Nathon"],
        placesEn: ["Bangkok Bank Tesco Lotus Chaweng", "Bangkok Bank Central Festival Samui", "KBank Central Festival", "SCB Chaweng", "Krungsri Lamai", "Krungthai Nathon"],
        costNoteTh: "เหมือนทั่วประเทศ — แต่บางสาขาต้องนัด 1-2 วันสำหรับเอ็กซ์แพท",
        costNoteEn: "Same as nationwide — but some branches require a 1-2 day appointment for expats",
        tipsTh: [
          "Bangkok Bank Tesco Lotus Chaweng เปิดง่ายสุด — มี English staff",
          "ใช้ Certificate of Residence จาก สตม. นาทอน (300 บาท, 1 วัน)",
          "Elite Visa ลัดได้ทุกสาขา ไม่ต้องรอนัด",
        ],
        tipsEn: [
          "Bangkok Bank Tesco Lotus Chaweng is easiest — English-speaking staff",
          "Use a Certificate of Residence from Nathon Immigration (THB 300, same-day)",
          "Elite Visa skips the queue at every branch",
        ],
      },
      schools: {
        introTh: "เกาะสมุยมีโรงเรียนนานาชาติ 3 แห่ง — International School of Samui (British/IB, 350,000), Lamai International (mixed, 220,000), PIA (mixed) เหมาะครอบครัวที่ลงหลักปักฐานยาว",
        introEn: "3 international schools — International School of Samui (British/IB, THB 350k), Lamai International (mixed, 220k), PIA (mixed). Best for families settling long-term.",
        placesTh: ["International School of Samui (ISS) - บ่อผุด", "Lamai International School", "PIA Samui Bilingual", "Samui Phoenix International", "Oonrak International Samui", "Greenacre International (สาขา)"],
        placesEn: ["International School of Samui (ISS) - Bophut", "Lamai International School", "PIA Samui Bilingual", "Samui Phoenix International", "Oonrak International Samui", "Greenacre International (branch)"],
        costNoteTh: "Top 300,000-400,000 · Mid 180,000-280,000 · ถูกกว่ากรุงเทพ 40%",
        costNoteEn: "Top THB 300-400k · Mid THB 180-280k · 40% cheaper than Bangkok",
        tipsTh: [
          "ISS รับเด็กกลางเทอม — assessment online ก่อนย้าย",
          "เลือกบ้านในบ่อผุด-เฉวงเหนือใกล้ ISS รถติดน้อย",
          "Application fee 10,000-30,000 บาท ไม่คืนเงิน",
        ],
        tipsEn: [
          "ISS admits mid-term — book a remote assessment before relocating",
          "Bophut-North Chaweng housing is closest to ISS with light traffic",
          "Application fees THB 10-30k are non-refundable",
        ],
      },
      healthcare: {
        introTh: "Bangkok Hospital Samui และ Bandon International Hospital เป็น Top tier — มี ICU, dive medicine (รักษานักดำน้ำ), ห้องฉุกเฉิน 24/7 ค่ารักษาเทียบเท่าภูเก็ต Hyperbaric chamber อยู่ที่ Bangkok Hospital",
        introEn: "Bangkok Hospital Samui and Bandon International Hospital are top tier — ICU, dive medicine, 24/7 ER. Costs comparable to Phuket; the hyperbaric chamber is at Bangkok Hospital.",
        placesTh: ["Bangkok Hospital Samui - เฉวง", "Bandon International Hospital - บ่อผุด", "Thai International Hospital - บ่อผุด", "Samui International Hospital - เฉวง", "Koh Samui Hospital (รัฐ) - นาทอน", "Lamai Hospital"],
        placesEn: ["Bangkok Hospital Samui - Chaweng", "Bandon International Hospital - Bophut", "Thai International Hospital - Bophut", "Samui International Hospital - Chaweng", "Koh Samui Hospital (public) - Nathon", "Lamai Hospital"],
        costNoteTh: "OPD 1,200-2,400 · ห้องเดี่ยว 5,000-10,000/คืน · เทียบเท่าภูเก็ต",
        costNoteEn: "OPD THB 1,200-2,400 · private room THB 5,000-10,000/night · on par with Phuket",
        tipsTh: [
          "ดำน้ำเกาะเต่า/เกาะพะงัน — มี hyperbaric chamber ที่ Bangkok Hospital Samui",
          "ประกัน Cigna/Allianz รับ direct billing",
          "ฉุกเฉินหนัก — Helicopter evac ไปกรุงเทพ Bangkok Hospital",
        ],
        tipsEn: [
          "Diving Koh Tao/Koh Phangan — hyperbaric chamber at Bangkok Hospital Samui",
          "Cigna/Allianz direct-bill",
          "Major emergencies — helicopter evac to Bangkok Hospital HQ",
        ],
      },
    },
  },

  // ============= 7. KRABI =============
  {
    slug: "krabi",
    nameTh: "กระบี่",
    nameEn: "Krabi",
    region: "ภาคใต้",
    lat: 8.0863,
    lng: 98.9063,
    profileTh: "กระบี่เป็นจังหวัดชายฝั่งอันดามันที่ค่าครองชีพต่ำกว่าภูเก็ต 25% มีเอ็กซ์แพท ~8,000 คน (สวีเดน เยอรมัน อังกฤษ) สนามบินกระบี่บินตรง 6 ประเทศ เน้นครอบครัวสงบและนักท่องเที่ยวยาว",
    profileEn: "Krabi sits on the Andaman coast — 25% cheaper than Phuket, ~8,000 expats (Swedish, German, British). Krabi Airport offers direct flights to 6 countries. Suited to quiet families and long-stay travellers.",
    expatCommunitiesTh: "สแกนดิเนเวีย (อ่าวนาง), เยอรมัน (กระบี่ทาวน์-น้ำใส), อังกฤษ-ออสซี่ (อ่าวนาง-เกาะลันตา)",
    expatCommunitiesEn: "Scandinavian (Ao Nang), German (Krabi Town-Nong Talay), British/Australian (Ao Nang-Koh Lanta)",
    districtsTh: ["อ่าวนาง (sea-view)", "กระบี่ทาวน์", "เกาะลันตา (เกษียณ-Digital Nomad)", "นพรัตน์ธารา", "น้ำใส (สงบ)", "เกาะพีพี"],
    districtsEn: ["Ao Nang (sea-view)", "Krabi Town", "Koh Lanta (retiree/digital nomad)", "Noppharat Thara", "Nong Talay (quiet)", "Koh Phi Phi"],
    transitTh: "รถยนต์/มอเตอร์ไซค์ — Grab จำกัด, รถสองแถวอ่าวนาง-กระบี่ทาวน์ 50 บาท, เรือเฟอร์รี่ไปเกาะลันตา 250 บาท",
    transitEn: "Cars/motorbikes — limited Grab, songthaew Ao Nang-Krabi Town THB 50, ferry to Koh Lanta THB 250",
    climateTh: "Tropical — ฝน เม.ย.-พ.ย. (มรสุมตะวันตกเฉียงใต้) ฤดูสูง ธ.ค.-มี.ค.",
    climateEn: "Tropical — rain Apr-Nov (SW monsoon), high season Dec-Mar",
    internetTh: "Fiber 500 Mbps-1 Gbps 690-990 บาท · 5G ในกระบี่ทาวน์-อ่าวนาง",
    internetEn: "500 Mbps-1 Gbps fiber THB 690-990 · 5G in Krabi Town-Ao Nang",
    costIndex: 65,
    categories: {
      housing: {
        introTh: "ตลาดเช่าเล็กแต่ราคาดี — สตูดิโอ 6,000-10,000, 1 ห้องนอน sea-view 12,000-22,000, วิลล่า private pool 3 ห้องนอน 28,000-55,000 บาท เลือก Krabi Property Group / SiamCondo",
        introEn: "Small but well-priced market — studios THB 6-10k, 1-bed sea-view THB 12-22k, 3-bed private-pool villas THB 28-55k. Try Krabi Property Group or SiamCondo.",
        placesTh: ["Aonang Cliff Beach", "Centara Anda Dhevi Suite", "Banyan Sea View", "Krabi Riviera (วิลล่า)", "Saichon Mansion", "Lanta Sand Resort (รายเดือน)"],
        placesEn: ["Aonang Cliff Beach", "Centara Anda Dhevi Suite", "Banyan Sea View", "Krabi Riviera (villas)", "Saichon Mansion", "Lanta Sand Resort (monthly)"],
        costNoteTh: "ถูกกว่าภูเก็ต 25% · sea-view อ่าวนาง premium",
        costNoteEn: "25% cheaper than Phuket · Ao Nang sea-view at premium",
        tipsTh: [
          "เกาะลันตา 35% ถูกกว่าอ่าวนาง — เหมาะ Digital Nomad",
          "เซ็นต์ก่อนพฤศจิกายน — ฤดูสูง ราคาขึ้น 40%",
          "ขนของจากกรุงเทพมาทางรถบรรทุก 8,000-15,000 บาท",
        ],
        tipsEn: [
          "Koh Lanta is 35% cheaper than Ao Nang — great for digital nomads",
          "Sign before November — high season pushes rates +40%",
          "Truck shipping from Bangkok runs THB 8-15k",
        ],
      },
      banking: {
        introTh: "Bangkok Bank, KBank, SCB สาขาในกระบี่ทาวน์, อ่าวนาง, เกาะลันตา เปิดบัญชีง่ายกว่าสมุย — ขอ Long-stay visa + lease 6 เดือน + Certificate of Residence จาก สตม. กระบี่",
        introEn: "Bangkok Bank, KBank, SCB branches in Krabi Town, Ao Nang and Koh Lanta. Easier than Samui — long-stay visa + 6-month lease + Certificate of Residence from Krabi Immigration.",
        placesTh: ["Bangkok Bank Maharaj Krabi", "Bangkok Bank Tesco Lotus Krabi", "KBank Vogue Krabi", "SCB Krabi Town", "Krungsri Ao Nang", "Krungthai Koh Lanta"],
        placesEn: ["Bangkok Bank Maharaj Krabi", "Bangkok Bank Tesco Lotus Krabi", "KBank Vogue Krabi", "SCB Krabi Town", "Krungsri Ao Nang", "Krungthai Koh Lanta"],
        costNoteTh: "เหมือนทั่วประเทศ — ATM เกาะลันตามีน้อย",
        costNoteEn: "Same as nationwide — limited ATMs on Koh Lanta",
        tipsTh: [
          "Bangkok Bank Maharaj เปิดง่ายสุด — staff รับเอ็กซ์แพทประจำ",
          "เกาะลันตา ใช้บริการ KBank online + ATM โบ๊เบ๊ก็เพียงพอ",
          "Certificate of Residence ที่ สตม. กระบี่ทาวน์ 1 วัน 300 บาท",
        ],
        tipsEn: [
          "Bangkok Bank Maharaj is the easiest — staff regularly help expats",
          "On Koh Lanta, KBank online + the Bo Boe ATM is usually enough",
          "Certificate of Residence at Krabi Town Immigration — same day, THB 300",
        ],
      },
      schools: {
        introTh: "กระบี่มีโรงเรียนนานาชาติ 2 แห่ง — Krabi International School (British, 220,000), Aonang International (mixed) ตัวเลือกน้อย — ครอบครัวต้องการ Tier 1 อาจต้องส่งภูเก็ต BIS หรือเรียน homeschool",
        introEn: "2 international schools — Krabi International School (British, THB 220k) and Aonang International (mixed). Limited options — families wanting Tier 1 may need to commute to BIS Phuket or homeschool.",
        placesTh: ["Krabi International School (KIS)", "Aonang International School", "Lanta International (สาขา)", "Akhanetra International School (สาขา ตรัง)", "Phuket BIS (สำหรับครอบครัวที่พร้อมเดินทาง)", "International Pioneers School (สาขา ตรัง)"],
        placesEn: ["Krabi International School (KIS)", "Aonang International School", "Lanta International (branch)", "Akhanetra International (Trang)", "BIS Phuket (commute option)", "International Pioneers (Trang)"],
        costNoteTh: "Top 200,000-280,000 · Mid 150,000-200,000 · ถูกที่สุดในเครือ expat",
        costNoteEn: "Top THB 200-280k · Mid THB 150-200k · cheapest among expat hubs",
        tipsTh: [
          "KIS รับเด็กกลางเทอม — Cambridge Primary curriculum",
          "ครอบครัวต้องการ IB อาจส่ง UWC Phuket boarding (Y9+)",
          "Homeschool ก็เป็นทางเลือก — กระบี่มีกลุ่มผู้ปกครอง 30+ ครอบครัว",
        ],
        tipsEn: [
          "KIS admits mid-term — Cambridge Primary curriculum",
          "IB-focused families can board at UWC Phuket from Y9",
          "Homeschooling is an option — 30+ parent families in Krabi",
        ],
      },
      healthcare: {
        introTh: "Krabi Nakharin International Hospital เป็น Top เอกชน — ICU, ER 24/7 Krabi Hospital รัฐคุณภาพดี เกาะลันตา มี Lanta Hospital เล็ก — เคสหนักต้องส่งกระบี่ทาวน์",
        introEn: "Krabi Nakharin International Hospital is the top private option — ICU, 24/7 ER. Krabi Hospital (public) is solid. Koh Lanta has a small Lanta Hospital — major cases transfer to Krabi Town.",
        placesTh: ["Krabi Nakharin International - กระบี่ทาวน์", "Krabi Hospital (รัฐ)", "Wattanapat Hospital - กระบี่ทาวน์", "Lanta Hospital (เล็ก)", "Aonang Krabi Hospital (cliniclevel)", "Phangnga International (สาขา)"],
        placesEn: ["Krabi Nakharin International - Krabi Town", "Krabi Hospital (public)", "Wattanapat Hospital - Krabi Town", "Lanta Hospital (small)", "Aonang Krabi Hospital (clinic-level)", "Phangnga International (nearby)"],
        costNoteTh: "OPD 800-1,800 · ห้องเดี่ยว 3,000-6,500/คืน · ถูกกว่าภูเก็ต 25%",
        costNoteEn: "OPD THB 800-1,800 · private room THB 3,000-6,500/night · 25% cheaper than Phuket",
        tipsTh: [
          "ดำน้ำเกาะพีพี-เกาะห้อง — hyperbaric chamber ใกล้สุดอยู่ภูเก็ต",
          "ประกันต้องครอบคลุม inter-hospital transfer (ภูเก็ต-สมุย-กรุงเทพ)",
          "Wattanapat รับเอ็กซ์แพท — English speaking",
        ],
        tipsEn: [
          "Diving Koh Phi Phi-Koh Hong — nearest hyperbaric chamber is on Phuket",
          "Insurance must cover inter-hospital transfer (Phuket-Samui-Bangkok)",
          "Wattanapat is expat-friendly — English speaking staff",
        ],
      },
    },
  },

  // ============= 8. KHON KAEN =============
  {
    slug: "khon-kaen",
    nameTh: "ขอนแก่น",
    nameEn: "Khon Kaen",
    region: "ภาคอีสาน",
    lat: 16.4419,
    lng: 102.8359,
    profileTh: "ขอนแก่นเป็นเมืองศูนย์กลางอีสานตอนกลาง — มีมหาวิทยาลัยขอนแก่น (มข.) เอ็กซ์แพท 5,000 คน (ครู, ลูกครึ่ง, นักวิจัยมข.) ค่าครองชีพต่ำที่สุดในอันดับเอ็กซ์แพทไทย สนามบิน KKC บินตรงในประเทศ",
    profileEn: "Khon Kaen is the central Isaan hub — home to Khon Kaen University (KKU) and ~5,000 foreigners (teachers, mixed families, KKU researchers). Cheapest cost of living among Thai expat cities; KKC offers domestic flights only.",
    expatCommunitiesTh: "อังกฤษ-อเมริกัน (TEFL teacher, รอบ มข.), จีน (ค้าขาย), เวียดนาม (แรงงาน), อินเดีย (medical, มข.)",
    expatCommunitiesEn: "British/American (TEFL teachers around KKU), Chinese (commerce), Vietnamese (workforce), Indian (medical/KKU)",
    districtsTh: ["รอบ มข. (มหาวิทยาลัย)", "เซ็นทรัล (ห้าง-สมัยใหม่)", "ในเมือง (ตลาด-วัฒนธรรม)", "บ้านเป็ด (หมู่บ้านใหม่)", "หนองโก (อุตสาหกรรม)", "ศิลา (บ้านเดี่ยว)"],
    districtsEn: ["Around KKU (university)", "Central (mall, modern)", "Old Town (markets, culture)", "Ban Ped (new estates)", "Nong Ko (industrial)", "Sila (single houses)"],
    transitTh: "รถยนต์/มอเตอร์ไซค์ — Grab มีน้อย, รถสองแถวรอบเมือง 10-15 บาท, มอเตอร์ไซค์เช่า 2,000 บาท/เดือน",
    transitEn: "Cars/motorbikes — limited Grab, in-town songthaew THB 10-15, motorbike rentals THB 2,000/month",
    climateTh: "Tropical — ร้อนแห้ง มี.ค.-พ.ค. (35-40°C) ฝน มิ.ย.-ต.ค. หนาว ธ.ค.-ก.พ. (15-25°C, อีสานหนาวสุด)",
    climateEn: "Tropical — hot/dry Mar-May (35-40°C), rain Jun-Oct, cool Dec-Feb (15-25°C, coldest in Isaan)",
    internetTh: "Fiber 1 Gbps 590 บาท · 5G ทั่วเมือง",
    internetEn: "1 Gbps fiber THB 590 · citywide 5G",
    costIndex: 45,
    categories: {
      housing: {
        introTh: "ตลาดเช่าถูกที่สุดในกลุ่มเอ็กซ์แพทไทย — สตูดิโอ 4,500-7,000, 1 ห้องนอน 7,000-12,000, บ้านเดี่ยว 2 ห้องนอน + บริเวณ 12,000-22,000 บาท เลือกรอบ มข. (ครู, นักศึกษา) หรือเซ็นทรัล (ห้าง)",
        introEn: "Cheapest expat rental market in Thailand — studios THB 4,500-7,000, 1-bed THB 7-12k, 2-bed houses with garden THB 12-22k. Pick around KKU (teachers/students) or Central (mall).",
        placesTh: ["The Base Heightz", "The Status Khon Kaen", "Penthouse Condo มข.", "Sila Place (บ้าน)", "Ban Ped Villa", "Centro Khon Kaen"],
        placesEn: ["The Base Heightz", "The Status Khon Kaen", "Penthouse Condo KKU", "Sila Place (houses)", "Ban Ped Villa", "Centro Khon Kaen"],
        costNoteTh: "ถูกกว่ากรุงเทพ 55% · บ้านเดี่ยวพร้อมที่ดินถูกที่สุด",
        costNoteEn: "55% cheaper than Bangkok · cheapest standalone houses with land",
        tipsTh: [
          "เช่าบ้าน 12,000 บาท/เดือน เท่าสตูดิโอใจกลางสุขุมวิท",
          "รอบ มข. มีตัวเลือกถูก แต่นักศึกษาเสียงดัง — เลือกชั้นสูง",
          "ค่าน้ำค่าไฟ MEA เรทบ้าน 4-5 บาท/หน่วย ถูกที่สุด",
        ],
        tipsEn: [
          "A THB 12,000 house equals a Sukhumvit studio",
          "Around KKU is cheap but noisy with students — pick higher floors",
          "MEA residential electricity THB 4-5/unit is the cheapest",
        ],
      },
      banking: {
        introTh: "Bangkok Bank, KBank, SCB สาขา Central Khon Kaen, มข., Big C เปิดง่าย — ครูภาษาใช้ใบรับรองโรงเรียน + Non-B Visa เปิดบัญชีใน 1 วัน",
        introEn: "Bangkok Bank, KBank, SCB branches at Central Khon Kaen, KKU, Big C are easy — TEFL teachers can open accounts same-day with a school letter + Non-B visa.",
        placesTh: ["Bangkok Bank Central Khon Kaen", "KBank Central Plaza", "SCB มข.", "Krungsri Big C Khon Kaen", "Krungthai มหาวิทยาลัย", "TTB ในเมือง"],
        placesEn: ["Bangkok Bank Central Khon Kaen", "KBank Central Plaza", "SCB KKU", "Krungsri Big C Khon Kaen", "Krungthai University", "TTB Mueang"],
        costNoteTh: "เหมือนทั่วประเทศ — ATM ทุก 500 ม. ในเมือง",
        costNoteEn: "Same as nationwide — ATMs every 500m downtown",
        tipsTh: [
          "ครูภาษา TEFL ใช้สัญญา 1 ปี + Non-B = เปิดได้ทุกธนาคาร",
          "Wise + KBank ดีสำหรับโอนเงินเดือนจากต่างประเทศ",
          "Bangkok Bank Central มี English speaking expat desk",
        ],
        tipsEn: [
          "TEFL teachers: 1-year contract + Non-B = open any bank",
          "Wise + KBank works for foreign payroll inflows",
          "Bangkok Bank Central has an English expat desk",
        ],
      },
      schools: {
        introTh: "ขอนแก่นมีโรงเรียนนานาชาติ 2 แห่ง — Khon Kaen International School (American, 220,000), International Community School Khon Kaen (British, 180,000) ตัวเลือกน้อย — ครอบครัว Tier 1 ต้องส่งกรุงเทพ",
        introEn: "2 international schools — Khon Kaen International School (American, THB 220k) and International Community School Khon Kaen (British, 180k). Limited choice — Tier 1 families relocate to Bangkok.",
        placesTh: ["Khon Kaen International School (KKIS)", "International Community School Khon Kaen", "Khon Kaen Wittayayon (สาธิต มข.)", "Watcharin English Program", "GIS Khon Kaen", "Anuban Khon Kaen IP (bilingual)"],
        placesEn: ["Khon Kaen International School (KKIS)", "International Community School Khon Kaen", "Khon Kaen Wittayayon (Satit KKU)", "Watcharin English Program", "GIS Khon Kaen", "Anuban Khon Kaen IP (bilingual)"],
        costNoteTh: "Top 200,000-280,000 · Mid 150,000-200,000 · bilingual โรงเรียนรัฐ 60,000-120,000",
        costNoteEn: "Top THB 200-280k · Mid THB 150-200k · public bilingual schools THB 60-120k",
        tipsTh: [
          "Satit มข. (สาธิต) เป็น Bilingual คุณภาพดี — ค่าเทอม 80,000 บาท",
          "KKIS รับเด็กกลางเทอม — Cambridge IGCSE + American HS Diploma",
          "ครอบครัว IB อาจส่ง Prem Tinsulanonda เชียงใหม่ boarding",
        ],
        tipsEn: [
          "Satit KKU is a quality bilingual at THB 80,000/year",
          "KKIS admits mid-term — Cambridge IGCSE + American HS diploma",
          "IB families can board at Prem Tinsulanonda in Chiang Mai",
        ],
      },
      healthcare: {
        introTh: "ขอนแก่น Hospital มข. (Srinagarind) เป็น 1 ใน 5 รพ.มหาวิทยาลัยที่ดีที่สุดในไทย — ผู้เชี่ยวชาญทุกสาขา ส่วน Khon Kaen Ram, Bangkok Hospital Khon Kaen เป็นเอกชน Top",
        introEn: "Srinagarind Hospital (KKU) is one of Thailand's top 5 university hospitals — specialists in every field. Khon Kaen Ram and Bangkok Hospital Khon Kaen lead the private tier.",
        placesTh: ["Srinagarind Hospital (รพ.มข.) - มข.", "Bangkok Hospital Khon Kaen", "Khon Kaen Ram Hospital", "Khon Kaen Hospital (รัฐ)", "Ratchaphruek Hospital", "AEK Udon Hospital (สาขา)"],
        placesEn: ["Srinagarind Hospital (KKU)", "Bangkok Hospital Khon Kaen", "Khon Kaen Ram Hospital", "Khon Kaen Hospital (public)", "Ratchaphruek Hospital", "AEK Udon Hospital (nearby)"],
        costNoteTh: "OPD 600-1,500 · ห้องเดี่ยว 2,500-5,500/คืน · ถูกกว่ากรุงเทพ 45%",
        costNoteEn: "OPD THB 600-1,500 · private room THB 2,500-5,500/night · 45% cheaper than Bangkok",
        tipsTh: [
          "Srinagarind ทุกสาขา — โรคซับซ้อน-มะเร็ง 1 ใน 5 ของไทย",
          "Bangkok Hospital Khon Kaen รับ direct billing 20+ ประกัน",
          "ราคาผ่าตัดถูกกว่ากรุงเทพ 30-40% — medical tourism ในประเทศ",
        ],
        tipsEn: [
          "Srinagarind handles every specialty — top-5 nationally for complex cases/oncology",
          "Bangkok Hospital Khon Kaen direct-bills 20+ insurers",
          "Surgeries are 30-40% cheaper than Bangkok — in-country medical tourism",
        ],
      },
    },
  },

  // ============= 9. UDON THANI =============
  {
    slug: "udon-thani",
    nameTh: "อุดรธานี",
    nameEn: "Udon Thani",
    region: "ภาคอีสาน",
    lat: 17.4138,
    lng: 102.7873,
    profileTh: "อุดรธานีเป็นเมืองอันดับ 2 ของอีสาน — มีเอ็กซ์แพท 8,000+ คน (อเมริกัน veteran, ลูกครึ่ง, ครู, นักธุรกิจชายแดนลาว) สนามบินอุดร (UTH) บินตรงในประเทศ ใกล้หนองคาย-เวียงจันทน์",
    profileEn: "Udon Thani is Isaan's #2 city — 8,000+ foreigners (American veterans, mixed families, teachers, Laos border businesses). UTH airport offers domestic flights; close to Nong Khai-Vientiane.",
    expatCommunitiesTh: "อเมริกัน veteran (เก่า base, ในเมือง), อังกฤษ-ออสซี่ (เซ็นทรัล), จีน (ค้าขาย), ลาว (ข้ามแดน), เกาหลี (ร้านอาหาร-โรงงาน)",
    expatCommunitiesEn: "American veterans (old base, downtown), British/Australian (Central), Chinese (commerce), Lao (cross-border), Korean (restaurants/factories)",
    districtsTh: ["ในเมือง (ตลาด-วัฒนธรรม)", "เซ็นทรัล พลาซ่า", "บ้านจั่น (บ้านเดี่ยว)", "หนองสมบูรณ์", "นาดี (สงบ)", "หนองหาน"],
    districtsEn: ["Old Town", "Central Plaza", "Ban Chan (single houses)", "Nong Somboon", "Na Di (quiet)", "Nong Han"],
    transitTh: "รถยนต์/มอเตอร์ไซค์ — Grab มีจำกัด, รถสองแถวรอบเมือง 10-20 บาท, มอเตอร์ไซค์เช่า 2,000 บาท/เดือน, รถบัสไปเวียงจันทน์ 1.5 ชม.",
    transitEn: "Cars/motorbikes — limited Grab, in-town songthaew THB 10-20, motorbike rentals THB 2,000/month, bus to Vientiane 1.5h",
    climateTh: "Tropical อีสาน — ร้อนแห้ง มี.ค.-พ.ค. (40°C+) หนาว ธ.ค.-ก.พ. (10-22°C) ฝน มิ.ย.-ต.ค.",
    climateEn: "Isaan tropical — hot/dry Mar-May (40°C+), cool Dec-Feb (10-22°C), rain Jun-Oct",
    internetTh: "Fiber 1 Gbps 590-690 บาท · 5G ในเมือง",
    internetEn: "1 Gbps fiber THB 590-690 · in-town 5G",
    costIndex: 50,
    categories: {
      housing: {
        introTh: "ตลาดเช่าถูก — สตูดิโอ 4,500-7,500, 1 ห้องนอน 8,000-13,000, บ้านเดี่ยว 2 ห้องนอน + บริเวณ 12,000-25,000 บาท เอ็กซ์แพทอเมริกัน veteran เช่าบ้านพร้อมสนามใหญ่",
        introEn: "Cheap rental market — studios THB 4,500-7,500, 1-bed THB 8-13k, 2-bed houses with garden THB 12-25k. American veterans favour houses with large yards.",
        placesTh: ["The Niche Udon", "Lumpini Place Udon", "Centro Udon", "Baan Wisut (บ้าน)", "Naa Dee Village", "City Park Udon"],
        placesEn: ["The Niche Udon", "Lumpini Place Udon", "Centro Udon", "Baan Wisut (houses)", "Naa Dee Village", "City Park Udon"],
        costNoteTh: "ถูกกว่ากรุงเทพ 50% · บ้านพร้อมสนาม 1 ไร่ 18,000 บาท",
        costNoteEn: "50% cheaper than Bangkok · houses on 1-rai land THB 18k",
        tipsTh: [
          "อเมริกัน veteran community ใหญ่ — กลุ่ม Facebook 'Udon Expats'",
          "เช่า 6 เดือน+ ลด 10-15%",
          "ใกล้สนามบิน UTH ใช้เวลา 10 นาที — สะดวกบินไป กทม.",
        ],
        tipsEn: [
          "Large American veteran community — 'Udon Expats' Facebook group",
          "6+ month leases save 10-15%",
          "UTH airport is a 10-min drive — easy Bangkok hops",
        ],
      },
      banking: {
        introTh: "Bangkok Bank, KBank, SCB สาขา Central Udon, UD Town, Big C เปิดง่าย — Retirement Visa + lease 6 เดือน บางสาขามี English speaking สำหรับเอ็กซ์แพท veteran",
        introEn: "Bangkok Bank, KBank, SCB branches at Central Udon, UD Town, Big C are easy — retirement visa + 6-month lease. Some branches have English-speaking staff for veteran expats.",
        placesTh: ["Bangkok Bank Central Plaza Udon", "Bangkok Bank UD Town", "KBank Central Plaza", "SCB UD Town", "Krungsri Big C", "Krungthai Old Town"],
        placesEn: ["Bangkok Bank Central Plaza Udon", "Bangkok Bank UD Town", "KBank Central Plaza", "SCB UD Town", "Krungsri Big C", "Krungthai Old Town"],
        costNoteTh: "เหมือนทั่วประเทศ — สาขา UD Town มี currency exchange",
        costNoteEn: "Same as nationwide — UD Town branch has currency exchange",
        tipsTh: [
          "Retirement Visa + 800,000 บาท + lease 6 เดือน = เปิด KBank ใน 1 วัน",
          "Bangkok Bank UD Town มี English speaking expat desk เกือบทุกวัน",
          "ATM ใกล้ Tesco Lotus, Central, UD Town เปิด 24 ชม.",
        ],
        tipsEn: [
          "Retirement visa + THB 800k + 6-month lease = same-day KBank account",
          "Bangkok Bank UD Town has an English expat desk most days",
          "24h ATMs at Tesco Lotus, Central, UD Town",
        ],
      },
      schools: {
        introTh: "อุดรธานีมีโรงเรียนนานาชาติ 1 แห่งหลัก — International School Udon Thani (American, 200,000) + bilingual โรงเรียนรัฐ Satit (60,000-100,000) ตัวเลือกจำกัด — ครอบครัว Tier 1 ส่งกรุงเทพหรือเชียงใหม่ boarding",
        introEn: "1 main international school — International School Udon Thani (American, THB 200k) + public bilingual schools (Satit, THB 60-100k). Limited choice — Tier 1 families relocate to Bangkok or board in Chiang Mai.",
        placesTh: ["International School Udon Thani (ISUT)", "Audom Suksa International", "Satit อุดรธานี (มหาวิทยาลัยราชภัฏ)", "Anuban Udon (bilingual)", "Garden International School (สาขา)", "Wittayayon Udon"],
        placesEn: ["International School Udon Thani (ISUT)", "Audom Suksa International", "Satit Udon (Rajabhat)", "Anuban Udon (bilingual)", "Garden International (branch)", "Wittayayon Udon"],
        costNoteTh: "Top 180,000-250,000 · bilingual 50,000-120,000 · ถูกที่สุดในเครือเอ็กซ์แพท",
        costNoteEn: "Top THB 180-250k · bilingual THB 50-120k · cheapest among expat hubs",
        tipsTh: [
          "ISUT รับเด็กกลางเทอม — American curriculum + Cambridge",
          "Satit อุดรเป็น bilingual คุณภาพดี — ครอบครัวลูกครึ่ง",
          "IB-focused → Prem Tinsulanonda boarding (Y7+)",
        ],
        tipsEn: [
          "ISUT admits mid-term — American curriculum + Cambridge",
          "Satit Udon is a quality bilingual — popular with mixed families",
          "IB-focused families board at Prem Tinsulanonda (Y7+)",
        ],
      },
      healthcare: {
        introTh: "AEK Udon International Hospital และ Bangkok Hospital Udon เป็น Top — ICU, ER 24/7, ภาษาอังกฤษ Udon Thani Hospital รัฐคุณภาพดี ค่ารักษาถูกกว่ากรุงเทพ 40%",
        introEn: "AEK Udon International Hospital and Bangkok Hospital Udon lead — ICU, 24/7 ER, English service. Udon Thani Hospital (public) is reliable; care 40% cheaper than Bangkok.",
        placesTh: ["AEK Udon International Hospital", "Bangkok Hospital Udon", "Udon Thani Hospital (รัฐ)", "Wattana Hospital Udon", "Nong Khai Hospital (สาขา ใกล้)", "Northeastern-Wattana Hospital"],
        placesEn: ["AEK Udon International Hospital", "Bangkok Hospital Udon", "Udon Thani Hospital (public)", "Wattana Hospital Udon", "Nong Khai Hospital (nearby)", "Northeastern-Wattana Hospital"],
        costNoteTh: "OPD 700-1,500 · ห้องเดี่ยว 2,500-5,000/คืน · ถูกกว่ากรุงเทพ 40%",
        costNoteEn: "OPD THB 700-1,500 · private room THB 2,500-5,000/night · 40% cheaper than Bangkok",
        tipsTh: [
          "AEK Udon มีกลุ่มเอ็กซ์แพท American veteran ประจำ — English fluent",
          "Bangkok Hospital Udon รับ direct billing 25+ ประกัน + Medicare-friendly",
          "เคสซับซ้อน — refer ไป Srinagarind ขอนแก่น 2 ชม.",
        ],
        tipsEn: [
          "AEK Udon serves a regular American veteran community — fluent English",
          "Bangkok Hospital Udon direct-bills 25+ insurers and is Medicare-friendly",
          "Complex cases refer to Srinagarind Khon Kaen (2h)",
        ],
      },
    },
  },

  // ============= 10. PAK KRET / NICHADA =============
  {
    slug: "pak-kret",
    nameTh: "ปากเกร็ด-นิชดาธานี",
    nameEn: "Pak Kret-Nichada Thani",
    region: "นนทบุรี",
    lat: 13.9221,
    lng: 100.4856,
    profileTh: "ปากเกร็ด-นิชดาธานี เป็นชุมชนเอ็กซ์แพทใหญ่ที่สุดในไทยนอกกรุงเทพชั้นใน — มี ISB (International School Bangkok) เป็น hub ครอบครัวอเมริกัน-ยุโรป 15,000+ คน อยู่ในนิชดาเป็น compound gated 1,500 บ้าน",
    profileEn: "Pak Kret-Nichada Thani is Thailand's largest expat community outside central Bangkok — anchored by International School Bangkok (ISB), home to 15,000+ American/European families in a 1,500-home gated Nichada compound.",
    expatCommunitiesTh: "อเมริกัน (นิชดา), อังกฤษ (นิชดา-ราชพฤกษ์), ญี่ปุ่น (ปากเกร็ด-แจ้งวัฒนะ), เกาหลี (เอกชัย), อินเดีย (ปากเกร็ด)",
    expatCommunitiesEn: "American (Nichada), British (Nichada-Ratchaphruek), Japanese (Pak Kret-Chaeng Watthana), Korean (Ekkachai), Indian (Pak Kret)",
    districtsTh: ["นิชดาธานี (gated, ISB)", "เมืองทอง (BTS Pink Line)", "แจ้งวัฒนะ (สนามบินดอนเมือง)", "ราชพฤกษ์ (บ้านเดี่ยว)", "ติวานนท์", "ปากเกร็ดเก่า"],
    districtsEn: ["Nichada Thani (gated, ISB)", "Muang Thong (BTS Pink Line)", "Chaeng Watthana (Don Mueang)", "Ratchaphruek (single houses)", "Tiwanon", "Old Pak Kret"],
    transitTh: "Pink Line MRT (เปิด 2024) เชื่อมเมืองทอง-แคราย, รถยนต์ส่วนตัวเป็นหลัก, Grab/Bolt, สนามบินดอนเมือง 20 นาที, สนามบินสุวรรณภูมิ 45 นาที",
    transitEn: "Pink Line MRT (opened 2024) connects Muang Thong-Khae Rai, cars dominate, Grab/Bolt, Don Mueang 20 min, Suvarnabhumi 45 min",
    climateTh: "เหมือนกรุงเทพ — ร้อนชื้นทั้งปี 28-35°C ฝน พ.ค.-ต.ค.",
    climateEn: "Same as Bangkok — hot/humid 28-35°C, rain May-Oct",
    internetTh: "Fiber 1 Gbps 590-890 บาท · 5G ครบ",
    internetEn: "1 Gbps fiber THB 590-890 · full 5G",
    costIndex: 95,
    categories: {
      housing: {
        introTh: "นิชดาธานีเป็น compound gated สำหรับครอบครัวอเมริกัน — บ้านเดี่ยว 4 ห้องนอน + สระว่ายน้ำ 80,000-180,000 บาท/เดือน + ค่า maintenance 8,000-15,000 ราชพฤกษ์ใกล้เคียง 50,000-120,000 ส่วนคอนโดเมืองทอง Pink Line 12,000-25,000",
        introEn: "Nichada Thani is a gated compound for American families — 4-bed pool houses THB 80-180k/month + THB 8-15k maintenance. Nearby Ratchaphruek THB 50-120k. Muang Thong Pink Line condos THB 12-25k.",
        placesTh: ["Nichada Thani Phase 1-3", "Magnolias Ratchadamri 47", "Hampton Thonglor 10 (สาขา)", "Plum Condo Phaholyothin", "The Lakes Pak Kret", "Centro Chaengwatthana"],
        placesEn: ["Nichada Thani Phase 1-3", "Magnolias Ratchadamri 47", "Hampton Thonglor 10 (branch)", "Plum Condo Phaholyothin", "The Lakes Pak Kret", "Centro Chaengwatthana"],
        costNoteTh: "นิชดา compound -10% จากใจกลางสุขุมวิทแต่บ้านใหญ่กว่า 3 เท่า · เมืองทอง -50%",
        costNoteEn: "Nichada compound is -10% vs central Sukhumvit but 3x bigger houses · Muang Thong -50%",
        tipsTh: [
          "นิชดามี waitlist 6-12 เดือน — Apply ผ่าน NCT Property Service",
          "ค่า maintenance รวม security 24/7 + สวน + คลับเฮาส์ + สระ",
          "เมืองทอง Pink Line คอนโดถูก + เดินทางใกล้ ISB ผ่าน Ratchaphruek 20 นาที",
        ],
        tipsEn: [
          "Nichada has a 6-12 month waitlist — apply through NCT Property Service",
          "Maintenance covers 24/7 security + landscaping + clubhouse + pool",
          "Muang Thong Pink Line condos are cheap and a 20-min Ratchaphruek hop to ISB",
        ],
      },
      banking: {
        introTh: "Bangkok Bank, KBank, SCB สาขา Central Chaeng Watthana, Nichada Plaza, Muang Thong เปิดบัญชีง่าย — มีบริการ English speaking ประจำ Nichada residents ใช้ ISB employment letter เปิดได้ทันที",
        introEn: "Bangkok Bank, KBank, SCB branches at Central Chaeng Watthana, Nichada Plaza and Muang Thong are easy — English-speaking staff. Nichada residents can open accounts immediately with an ISB employment letter.",
        placesTh: ["Bangkok Bank Nichada Plaza", "Bangkok Bank Central Chaeng Watthana", "KBank The Mall Ngamwongwan", "SCB Muang Thong", "Krungsri Tiwanon", "Krungthai Pak Kret"],
        placesEn: ["Bangkok Bank Nichada Plaza", "Bangkok Bank Central Chaeng Watthana", "KBank The Mall Ngamwongwan", "SCB Muang Thong", "Krungsri Tiwanon", "Krungthai Pak Kret"],
        costNoteTh: "เหมือนกรุงเทพ — Nichada Plaza มีพนักงานพูดอังกฤษเต็มเวลา",
        costNoteEn: "Same as Bangkok — Nichada Plaza has full-time English-speaking staff",
        tipsTh: [
          "Bangkok Bank Nichada Plaza เปิดเฉพาะเอ็กซ์แพท — fast track 30 นาที",
          "ISB staff letter + passport = เปิดบัญชีทุกธนาคาร",
          "USD/EUR savings ที่ Bangkok Bank Nichada — โอนเงินครอบครัวจากต่างประเทศง่าย",
        ],
        tipsEn: [
          "Bangkok Bank Nichada Plaza is expat-only with a 30-min fast track",
          "ISB staff letter + passport = open at any bank",
          "USD/EUR savings at Bangkok Bank Nichada — easy inbound family transfers",
        ],
      },
      schools: {
        introTh: "นิชดาเป็นบ้านของ ISB (International School Bangkok) — โรงเรียนนานาชาติเก่าแก่ (1957, American curriculum, IB) ค่าเทอม 850,000-1,100,000 บาท + รอบรัศมี 5 กม. มี ISB Auxiliary, KIS, Wellington College, Concordian, Heathfield",
        introEn: "Nichada is home to International School Bangkok (ISB) — Thailand's oldest international school (1957, American + IB) at THB 850k-1.1M. Within 5 km: ISB Auxiliary, KIS, Wellington College, Concordian, Heathfield.",
        placesTh: ["International School Bangkok (ISB) - นิชดาธานี", "KIS International School - แจ้งวัฒนะ", "Wellington College International Bangkok - กรุงเทพกรีฑา (สาขา)", "Concordian International - บางนา (สาขา)", "Heathfield International School - ปากเกร็ด", "Modulo International - แจ้งวัฒนะ"],
        placesEn: ["International School Bangkok (ISB) - Nichada", "KIS International School - Chaeng Watthana", "Wellington College International Bangkok (branch)", "Concordian International (Bang Na branch)", "Heathfield International School - Pak Kret", "Modulo International - Chaeng Watthana"],
        costNoteTh: "ISB top 950,000-1,100,000 · KIS 700,000-900,000 · Heathfield 250,000-380,000",
        costNoteEn: "ISB top THB 950k-1.1M · KIS THB 700-900k · Heathfield THB 250-380k",
        tipsTh: [
          "ISB waitlist 18-24 เดือน Y1-Y5 — Apply ตั้งแต่ลูก 2 ขวบ",
          "ISB Auxiliary (Y10+) มี slot เปิดเร็วกว่า — สมัครก่อน 6 เดือน",
          "Application fee ISB 50,000 บาท + Capital Levy 200,000 (refundable)",
        ],
        tipsEn: [
          "ISB has an 18-24 month Y1-Y5 waitlist — apply when your child turns 2",
          "ISB Auxiliary (Y10+) has faster openings — apply 6 months out",
          "ISB application fee THB 50k + refundable Capital Levy THB 200k",
        ],
      },
      healthcare: {
        introTh: "Bangkok Hospital Chaeng Watthana และ Vibhavadi Pak Kret เป็น Top เอกชน — รพ.พระมงกุฎเกล้า ใกล้เคียง (รัฐ) ค่ารักษาเทียบเท่ากรุงเทพชั้นใน Nichada residents มักใช้ Bumrungrad/BNH ในเมือง",
        introEn: "Bangkok Hospital Chaeng Watthana and Vibhavadi Pak Kret lead the private tier; nearby Phramongkutklao is the major public hospital. Costs match central Bangkok; Nichada residents often commute to Bumrungrad/BNH downtown.",
        placesTh: ["Bangkok Hospital Chaeng Watthana", "Vibhavadi Pak Kret", "Mongkutwattana Hospital - งามวงศ์วาน", "Saint Carlos Hospital Pak Kret", "Praram 9 Hospital (สาขา ใกล้)", "Yanhee Hospital - ทุ่งบางเขน"],
        placesEn: ["Bangkok Hospital Chaeng Watthana", "Vibhavadi Pak Kret", "Mongkutwattana Hospital - Ngamwongwan", "Saint Carlos Hospital Pak Kret", "Praram 9 Hospital (nearby)", "Yanhee Hospital - Bang Khen"],
        costNoteTh: "OPD 1,200-2,800 · ห้องเดี่ยว 4,500-9,000/คืน · เหมือนกรุงเทพชั้นใน",
        costNoteEn: "OPD THB 1,200-2,800 · private room THB 4,500-9,000/night · matches central Bangkok",
        tipsTh: [
          "Bangkok Hospital Chaeng Watthana รับ direct billing 35+ ประกัน",
          "Bumrungrad ระยะ 25 นาที — Pink Line + BTS ไป EmQuartier เปลี่ยน",
          "ISB partnership กับ Bumrungrad — direct billing student wellness",
        ],
        tipsEn: [
          "Bangkok Hospital Chaeng Watthana direct-bills 35+ insurers",
          "Bumrungrad is a 25-min ride — Pink Line + BTS via EmQuartier",
          "ISB partners with Bumrungrad — direct billing for student wellness",
        ],
      },
    },
  },
];

export const EXPAT_CITY_SLUGS = EXPAT_CITIES.map((c) => c.slug);

export function findExpatCity(slug: string) {
  return EXPAT_CITIES.find((c) => c.slug === slug);
}
